// cspell:disable

import { Currency } from "@redotech/money/currencies";
import type { CheckoutShippingOption } from "@redotech/redo-conversion-checkout-shipping-common/checkout-shipping-option";
import {
  DynamicRateRounding,
  RateTableType,
} from "@redotech/redo-conversion-checkout-shipping-common/shipping-rate-table-enums";
import {
  AsendiaServices,
  AustraliaPostServices,
  CanparServices,
  ChitChatsServices,
  CorrelatedCarrierService,
  CorrelatedCarrierServiceSchema,
  EcomExpressServices,
  ETAExpressServices,
  FirstMileServices,
  FleetOpticsServices,
  GlobalEServices,
  GlsServices,
  IcsCourierServices,
  IntelcomServices,
  LandmarkGlobalServices,
  OnTracServices,
  PassportGlobalServices,
  PostNLServices,
  RoyalMailV3Services,
  Service,
  StallionExpressServices,
  TiktokShopServices,
  UniUniServices,
  UpsMailInnovationsServices,
  VehoServices,
  WhizDeliveryServices,
} from "@redotech/redo-fulfillment-carrier-common/carrier-service-catalog";
import {
  AmazonServices,
  ApcServices,
  CanadaPostServices,
  Carriers,
  DhlEcsServices,
  DhlExpressServices,
  EPostGlobalServices,
  FedexServices,
  PurolatorServices,
  SpeeDeeServices,
  UpsServices,
  UspsServices,
} from "@redotech/redo-fulfillment-carrier-common/carrier-service-enums";
import {
  isAmazonService,
  isApcService,
  isAsendiaService,
  isAustraliaPostService,
  isCanadaPostService,
  isCanparService,
  isChitChatsService,
  isDhlEcsService,
  isDhlExpressService,
  isEcomExpressService,
  isEPostGlobalService,
  isETAExpressService,
  isFedexService,
  isFirstMileService,
  isFleetOpticsService,
  isGlobalEService,
  isGlsService,
  isIcsCourierService,
  isIntelcomService,
  isLandmarkGlobalService,
  isOnTracService,
  isPassportGlobalService,
  isPostNLService,
  isPurolatorService,
  isRoyalMailV3Service,
  isSpeeDeeService,
  isStallionExpressService,
  isTiktokShopService,
  isUniUniService,
  isUpsMailInnovationsService,
  isUpsService,
  isUspsService,
  isVehoService,
  isWhizDeliveryService,
} from "@redotech/redo-fulfillment-carrier-common/carriers-and-services";
import * as zExt from "@redotech/rpc/ext";
import { assertNever } from "@redotech/util/type";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import {
  SPECIAL_BLOCK_CHECKOUT_RATE_TABLE_ID,
  SPECIAL_BLOCK_CHECKOUT_SHIPPING_METHOD_CODE,
} from "../checkouts/block-checkout";
import { FIXED_TROJAN_HORSE_RATE_TABLE } from "../checkouts/checkout-ab-test-utils";
import {
  dynamicRateRoundingToServiceCodeDeserialization,
  isDynamicRateRoundingServiceCodeSerializedKey,
} from "./shipping-rate-table";
// Shared key constants + CompactServiceCodeKey are defined in the dependency-
// free ./service-code-lean module (single source of truth) so the checkout
// bundle can use them without importing this heavy module. Canonical-only
// keys (carrier/cost/markup) stay local below.
import {
  ASSIGNED_TREATMENT_IDS_KEY,
  CARRIER_SERVICE_KEY,
  CHECKOUT_TREE_NODE_ID_KEY,
  CompactServiceCodeKey,
  COST_BREAKDOWN_KEY,
  MAX_CURRENCY_DECIMALS,
  PROMISED_DELIVERY_DATE_IN_YYYYMMDD_KEY,
  PROVIDER_KEY,
  RATE_TABLE_ID_KEY,
} from "./service-code-lean";

const CARRIER_KEY = "c";
const SERVICE_KEY = "s";
const COST_CURRENCY_KEY = "cc";
const LABEL_COST_KEY = "lc";
const MARKUP_KEY = "m";
const MARKUP_PERCENTAGE_KEY = "mp";
const MARKUP_AMOUNT_KEY = "ma";
const ROUNDING_KEY = "ro";

enum CarrierServiceCodeConstant {
  FEDEX = "f",
  FEDEX_SMART_POST = "fsp",
  UPS = "ups",
  UPS_SUREPOST = "ups_sp",
  UPS_MAIL_INNOVATIONS = "ups_mi",
  USPS = "usps",
  USPS_RETURNS = "usps_ret",
  USPS_SHIP = "usps_redo",
  AMAZON = "ama",
  DHL_EXPRESS = "dhl_exp",
  VEHO = "veho",
  APC = "apc",
  FIRST_MILE = "fm",
  AUSTRALIA_POST = "ap",
  CANADA_POST = "cp",
  DHL_ECS = "dhl_ecs",
  GLS = "gls",
  ASENDIA = "asendia",
  ROYAL_MAIL_V3 = "rmv3",
  SPEE_DEE = "spee_dee",
  PASSPORT_GLOBAL = "passport_global",
  LANDMARK_GLOBAL = "lg",
  ETA_EXPRESS = "eta_exp",
  EPOST_GLOBAL = "epost_global",
  ONTRAC = "ontrac",
  GLOBALE = "globale",
  PUROLATOR = "pur",
  UNIUNI = "uniuni",
  STALLION_EXPRESS = "stallion",
  ECOM_EXPRESS = "ecom",
  CHITCHATS = "chitchats",
  POST_NL = "postnl",
  INTELCOM = "intelcom",
  FLEET_OPTICS = "fleetoptics",
  ICS_COURIER = "ics",
  WHIZ_DELIVERY = "whiz",
  TIKTOK_SHOP = "tiktok",
  CANPAR_EXPRESS = "cnp",
}

// Type check to ensure all Carriers are represented in CarrierServiceCodeConstant
// export to consider variable "used"
export const _carrierToCarrierServiceCodeConstantTypeCheck: Record<
  Carriers,
  CarrierServiceCodeConstant
> = {
  [Carriers.FEDEX]: CarrierServiceCodeConstant.FEDEX,
  [Carriers.FEDEX_SMART_POST]: CarrierServiceCodeConstant.FEDEX_SMART_POST,
  [Carriers.UPS]: CarrierServiceCodeConstant.UPS,
  [Carriers.UPS_SUREPOST]: CarrierServiceCodeConstant.UPS_SUREPOST,
  [Carriers.UPS_MAIL_INNOVATIONS]:
    CarrierServiceCodeConstant.UPS_MAIL_INNOVATIONS,
  [Carriers.USPS]: CarrierServiceCodeConstant.USPS,
  [Carriers.USPS_RETURNS]: CarrierServiceCodeConstant.USPS_RETURNS,
  [Carriers.USPS_SHIP]: CarrierServiceCodeConstant.USPS_SHIP,
  [Carriers.AMAZON]: CarrierServiceCodeConstant.AMAZON,
  [Carriers.DHL_EXPRESS]: CarrierServiceCodeConstant.DHL_EXPRESS,
  [Carriers.VEHO]: CarrierServiceCodeConstant.VEHO,
  [Carriers.APC]: CarrierServiceCodeConstant.APC,
  [Carriers.FIRST_MILE]: CarrierServiceCodeConstant.FIRST_MILE,
  [Carriers.AUSTRALIA_POST]: CarrierServiceCodeConstant.AUSTRALIA_POST,
  [Carriers.CANADA_POST]: CarrierServiceCodeConstant.CANADA_POST,
  [Carriers.DHL_ECS]: CarrierServiceCodeConstant.DHL_ECS,
  [Carriers.GLS]: CarrierServiceCodeConstant.GLS,
  [Carriers.ASENDIA]: CarrierServiceCodeConstant.ASENDIA,
  [Carriers.ROYAL_MAIL_V3]: CarrierServiceCodeConstant.ROYAL_MAIL_V3,
  [Carriers.SPEE_DEE]: CarrierServiceCodeConstant.SPEE_DEE,
  [Carriers.PASSPORT_GLOBAL]: CarrierServiceCodeConstant.PASSPORT_GLOBAL,
  [Carriers.LANDMARK_GLOBAL]: CarrierServiceCodeConstant.LANDMARK_GLOBAL,
  [Carriers.ETA_EXPRESS]: CarrierServiceCodeConstant.ETA_EXPRESS,
  [Carriers.EPOST_GLOBAL]: CarrierServiceCodeConstant.EPOST_GLOBAL,
  [Carriers.ONTRAC]: CarrierServiceCodeConstant.ONTRAC,
  [Carriers.GLOBALE]: CarrierServiceCodeConstant.GLOBALE,
  [Carriers.PUROLATOR]: CarrierServiceCodeConstant.PUROLATOR,
  [Carriers.UNIUNI]: CarrierServiceCodeConstant.UNIUNI,
  [Carriers.STALLION_EXPRESS]: CarrierServiceCodeConstant.STALLION_EXPRESS,
  [Carriers.ECOM_EXPRESS]: CarrierServiceCodeConstant.ECOM_EXPRESS,
  [Carriers.CHITCHATS]: CarrierServiceCodeConstant.CHITCHATS,
  [Carriers.POST_NL]: CarrierServiceCodeConstant.POST_NL,
  [Carriers.INTELCOM]: CarrierServiceCodeConstant.INTELCOM,
  [Carriers.FLEET_OPTICS]: CarrierServiceCodeConstant.FLEET_OPTICS,
  [Carriers.ICS_COURIER]: CarrierServiceCodeConstant.ICS_COURIER,
  [Carriers.WHIZ_DELIVERY]: CarrierServiceCodeConstant.WHIZ_DELIVERY,
  [Carriers.TIKTOK_SHOP]: CarrierServiceCodeConstant.TIKTOK_SHOP,
  [Carriers.CANPAR_EXPRESS]: CarrierServiceCodeConstant.CANPAR_EXPRESS,
};

enum FedexServiceCodeConstant {
  FEDEX_INTERNATIONAL_PRIORITY_EXPRESS = "f_a",
  FEDEX_INTERNATIONAL_CONNECT_PLUS = "f_b",
  INTERNATIONAL_FIRST = "f_c",
  FEDEX_GROUND = "f_d",
  FEDEX_2_DAY = "f_e",
  PRIORITY_OVERNIGHT = "f_f",
  FEDEX_FIRST_FREIGHT = "f_g",
  FEDEX_EXPRESS_SAVER = "f_h",
  FEDEX_3_DAY_FREIGHT = "f_i",
  FEDEX_2_DAY_AM = "f_j",
  FEDEX_2_DAY_FREIGHT = "f_k",
  INTERNATIONAL_ECONOMY = "f_l",
  FIRST_OVERNIGHT = "f_m",
  STANDARD_OVERNIGHT = "f_n",
  SMART_POST = "f_o",
  FEDEX_1_DAY_FREIGHT = "f_p",
  INTERNATIONAL_PRIORITY = "f_q",
  FEDEX_INTERNATIONAL_PRIORITY = "f_r",
  FEDEX_GROUND_HOME_DELIVERY = "f_s",
  EUROPE_FIRST_INTERNATIONAL_PRIORITY = "f_t",
  INTERNATIONAL_ECONOMY_FREIGHT = "f_u",
  INTERNATIONAL_PRIORITY_FREIGHT = "f_v",
  FEDEX_INTERNATIONAL_DEFERRED_FREIGHT = "f_w",
}
const fedexServiceToServiceCodeConstant: Record<
  FedexServices,
  FedexServiceCodeConstant
> = {
  [FedexServices.FEDEX_INTERNATIONAL_PRIORITY_EXPRESS]:
    FedexServiceCodeConstant.FEDEX_INTERNATIONAL_PRIORITY_EXPRESS,
  [FedexServices.FEDEX_INTERNATIONAL_CONNECT_PLUS]:
    FedexServiceCodeConstant.FEDEX_INTERNATIONAL_CONNECT_PLUS,
  [FedexServices.FEDEX_INTERNATIONAL_DEFERRED_FREIGHT]:
    FedexServiceCodeConstant.FEDEX_INTERNATIONAL_DEFERRED_FREIGHT,
  [FedexServices.INTERNATIONAL_FIRST]:
    FedexServiceCodeConstant.INTERNATIONAL_FIRST,
  [FedexServices.FEDEX_GROUND]: FedexServiceCodeConstant.FEDEX_GROUND,
  [FedexServices.FEDEX_2_DAY]: FedexServiceCodeConstant.FEDEX_2_DAY,
  [FedexServices.PRIORITY_OVERNIGHT]:
    FedexServiceCodeConstant.PRIORITY_OVERNIGHT,
  [FedexServices.FEDEX_FIRST_FREIGHT]:
    FedexServiceCodeConstant.FEDEX_FIRST_FREIGHT,
  [FedexServices.FEDEX_EXPRESS_SAVER]:
    FedexServiceCodeConstant.FEDEX_EXPRESS_SAVER,
  [FedexServices.FEDEX_3_DAY_FREIGHT]:
    FedexServiceCodeConstant.FEDEX_3_DAY_FREIGHT,
  [FedexServices.FEDEX_2_DAY_AM]: FedexServiceCodeConstant.FEDEX_2_DAY_AM,
  [FedexServices.FEDEX_2_DAY_FREIGHT]:
    FedexServiceCodeConstant.FEDEX_2_DAY_FREIGHT,
  [FedexServices.INTERNATIONAL_ECONOMY]:
    FedexServiceCodeConstant.INTERNATIONAL_ECONOMY,
  [FedexServices.FIRST_OVERNIGHT]: FedexServiceCodeConstant.FIRST_OVERNIGHT,
  [FedexServices.STANDARD_OVERNIGHT]:
    FedexServiceCodeConstant.STANDARD_OVERNIGHT,
  [FedexServices.SMART_POST]: FedexServiceCodeConstant.SMART_POST,
  [FedexServices.FEDEX_1_DAY_FREIGHT]:
    FedexServiceCodeConstant.FEDEX_1_DAY_FREIGHT,
  [FedexServices.INTERNATIONAL_PRIORITY]:
    FedexServiceCodeConstant.INTERNATIONAL_PRIORITY,
  [FedexServices.FEDEX_INTERNATIONAL_PRIORITY]:
    FedexServiceCodeConstant.FEDEX_INTERNATIONAL_PRIORITY,
  [FedexServices.FEDEX_GROUND_HOME_DELIVERY]:
    FedexServiceCodeConstant.FEDEX_GROUND_HOME_DELIVERY,
  [FedexServices.EUROPE_FIRST_INTERNATIONAL_PRIORITY]:
    FedexServiceCodeConstant.EUROPE_FIRST_INTERNATIONAL_PRIORITY,
  [FedexServices.INTERNATIONAL_ECONOMY_FREIGHT]:
    FedexServiceCodeConstant.INTERNATIONAL_ECONOMY_FREIGHT,
  [FedexServices.INTERNATIONAL_PRIORITY_FREIGHT]:
    FedexServiceCodeConstant.INTERNATIONAL_PRIORITY_FREIGHT,
};

enum UpsServiceCodeConstant {
  Ground = "ups_a",
  NextDayAir = "ups_b",
  ThreeDaySelect = "ups_c",
  SecondDayAirAM = "ups_d",
  UpsSaver = "ups_e",
  NextDayAirEarlyAM = "ups_f",
  ExpressPlus = "ups_g",
  Express = "ups_h",
  NextDayAirSaver = "ups_i",
  UPSStandard = "ups_j",
  SecondDayAir = "ups_k",
  Expedited = "ups_l",
  SurePostUnder1Lb = "ups_m",
  SurePostOver1Lb = "ups_n",
  ExpressEarly = "ups_o",
  ExpressSaver = "ups_p",
  CanadaExpress = "ups_q",
  CanadaExpedited = "ups_r",
}
const upsServiceToServiceCodeConstant: Record<
  UpsServices,
  UpsServiceCodeConstant
> = {
  [UpsServices.Ground]: UpsServiceCodeConstant.Ground,
  [UpsServices.NextDayAir]: UpsServiceCodeConstant.NextDayAir,
  [UpsServices.ThreeDaySelect]: UpsServiceCodeConstant.ThreeDaySelect,
  [UpsServices.SecondDayAirAM]: UpsServiceCodeConstant.SecondDayAirAM,
  [UpsServices.WorldWideSaver]: UpsServiceCodeConstant.UpsSaver,
  [UpsServices.NextDayAirEarlyAM]: UpsServiceCodeConstant.NextDayAirEarlyAM,
  [UpsServices.WorldWideExpressPlus]: UpsServiceCodeConstant.ExpressPlus,
  [UpsServices.WorldWideExpress]: UpsServiceCodeConstant.Express,
  [UpsServices.NextDayAirSaver]: UpsServiceCodeConstant.NextDayAirSaver,
  [UpsServices.UPSStandard]: UpsServiceCodeConstant.UPSStandard,
  [UpsServices.SecondDayAir]: UpsServiceCodeConstant.SecondDayAir,
  [UpsServices.WorldWideExpedited]: UpsServiceCodeConstant.Expedited,
  [UpsServices.SurePostUnder1Lb]: UpsServiceCodeConstant.SurePostUnder1Lb,
  [UpsServices.SurePostOver1Lb]: UpsServiceCodeConstant.SurePostOver1Lb,
  [UpsServices.ExpressEarly]: UpsServiceCodeConstant.ExpressEarly,
  [UpsServices.ExpressSaver]: UpsServiceCodeConstant.ExpressSaver,
  [UpsServices.CanadaExpress]: UpsServiceCodeConstant.CanadaExpress,
  [UpsServices.CanadaExpedited]: UpsServiceCodeConstant.CanadaExpedited,
};

enum AmazonServiceCodeConstant {
  Ground = "ama_a",
}
const amazonServiceToServiceCodeConstant: Record<
  AmazonServices,
  AmazonServiceCodeConstant
> = { [AmazonServices.Ground]: AmazonServiceCodeConstant.Ground };

enum VehoServiceCodeConstant {
  NextDay = "veho_a",
  SameDay = "veho_b",
  GroundPlus = "veho_c",
  PremiumEconomy = "veho_d",
  GroundPlusThree = "veho_e",
}
const vehoServiceToServiceCodeConstant: Record<
  VehoServices,
  VehoServiceCodeConstant
> = {
  [VehoServices.NextDay]: VehoServiceCodeConstant.NextDay,
  [VehoServices.SameDay]: VehoServiceCodeConstant.SameDay,
  [VehoServices.GroundPlus]: VehoServiceCodeConstant.GroundPlus,
  [VehoServices.PremiumEconomy]: VehoServiceCodeConstant.PremiumEconomy,
  [VehoServices.GroundPlusThree]: VehoServiceCodeConstant.GroundPlusThree,
};

enum ApcServiceCodeConstant {
  ParcelConnectBookService = "apc_a",
  ParcelConnectExpeditedDDP = "apc_b",
  ParcelConnectExpeditedDDU = "apc_c",
  ParcelConnectPriorityDDP = "apc_d",
  ParcelConnectPriorityDDPDelcon = "apc_e",
  ParcelConnectPriorityDDU = "apc_f",
  ParcelConnectPriorityDDUDelcon = "apc_g",
  ParcelConnectPriorityDDUPQW = "apc_h",
  ParcelConnectStandardDDU = "apc_i",
  ParcelConnectStandardDDUPQW = "apc_j",
  ParcelConnectPacketDDU = "apc_k",
  ApcEPMIDDP = "apc_l",
  ApcEPMEIDDP = "apc_m",
  ApcPacketDDP = "apc_n",
  ApcUSPSInternationalPriorityMailExpress = "apc_o",
  ApcDDUPlus = "apc_p",
  ApcPriorityDDUPlus = "apc_q",
  ApcPriorityDDUPlusDelcon = "apc_r",
  ApcEPacketDDUPlus = "apc_s",
  ApcPMIDDUPlus = "apc_t",
  ApcPriorityPQWPlus = "apc_u",
  ApcUSA = "apc_v",
  ApcUSAGround = "apc_w",
  ApcUSAExpress = "apc_x",
  ApcUSA2 = "apc_y",
  ApcUSA3 = "apc_z",
  ApcPriorityWorldwide = "apc_pw",
  ApcPriorityWorldwideTracked = "apc_pwt",
  ApcPriorityWorldwideTrackedDdp = "apc_pwtddp",
  ApcPriorityWorldwideExpedited = "apc_pwe",
  ApcPriorityWorldwideExpeditedDdp = "apc_pweddp",
}
const apcServiceToServiceCodeConstant: Record<
  ApcServices,
  ApcServiceCodeConstant
> = {
  [ApcServices.ParcelConnectBookService]:
    ApcServiceCodeConstant.ParcelConnectBookService,
  [ApcServices.ParcelConnectExpeditedDDP]:
    ApcServiceCodeConstant.ParcelConnectExpeditedDDP,
  [ApcServices.ParcelConnectExpeditedDDU]:
    ApcServiceCodeConstant.ParcelConnectExpeditedDDU,
  [ApcServices.ParcelConnectPriorityDDP]:
    ApcServiceCodeConstant.ParcelConnectPriorityDDP,
  [ApcServices.ParcelConnectPriorityDDPDelcon]:
    ApcServiceCodeConstant.ParcelConnectPriorityDDPDelcon,
  [ApcServices.ParcelConnectPriorityDDU]:
    ApcServiceCodeConstant.ParcelConnectPriorityDDU,
  [ApcServices.ParcelConnectPriorityDDUDelcon]:
    ApcServiceCodeConstant.ParcelConnectPriorityDDUDelcon,
  [ApcServices.ParcelConnectPriorityDDUPQW]:
    ApcServiceCodeConstant.ParcelConnectPriorityDDUPQW,
  [ApcServices.ParcelConnectStandardDDU]:
    ApcServiceCodeConstant.ParcelConnectStandardDDU,
  [ApcServices.ParcelConnectStandardDDUPQW]:
    ApcServiceCodeConstant.ParcelConnectStandardDDUPQW,
  [ApcServices.ParcelConnectPacketDDU]:
    ApcServiceCodeConstant.ParcelConnectPacketDDU,
  [ApcServices.ApcEPMIDDP]: ApcServiceCodeConstant.ApcEPMIDDP,
  [ApcServices.ApcEPMEIDDP]: ApcServiceCodeConstant.ApcEPMEIDDP,
  [ApcServices.ApcPacketDDP]: ApcServiceCodeConstant.ApcPacketDDP,
  [ApcServices.ApcUSPSInternationalPriorityMailExpress]:
    ApcServiceCodeConstant.ApcUSPSInternationalPriorityMailExpress,
  [ApcServices.ApcDDUPlus]: ApcServiceCodeConstant.ApcDDUPlus,
  [ApcServices.ApcPriorityDDUPlus]: ApcServiceCodeConstant.ApcPriorityDDUPlus,
  [ApcServices.ApcPriorityDDUPlusDelcon]:
    ApcServiceCodeConstant.ApcPriorityDDUPlusDelcon,
  [ApcServices.ApcEPacketDDUPlus]: ApcServiceCodeConstant.ApcEPacketDDUPlus,
  [ApcServices.ApcPMIDDUPlus]: ApcServiceCodeConstant.ApcPMIDDUPlus,
  [ApcServices.ApcPriorityPQWPlus]: ApcServiceCodeConstant.ApcPriorityPQWPlus,
  [ApcServices.ApcUSA]: ApcServiceCodeConstant.ApcUSA,
  [ApcServices.ApcUSAGround]: ApcServiceCodeConstant.ApcUSAGround,
  [ApcServices.ApcUSAExpress]: ApcServiceCodeConstant.ApcUSAExpress,
  [ApcServices.ApcUSA2]: ApcServiceCodeConstant.ApcUSA2,
  [ApcServices.ApcUSA3]: ApcServiceCodeConstant.ApcUSA3,
  [ApcServices.ApcPriorityWorldwide]:
    ApcServiceCodeConstant.ApcPriorityWorldwide,
  [ApcServices.ApcPriorityWorldwideTracked]:
    ApcServiceCodeConstant.ApcPriorityWorldwideTracked,
  [ApcServices.ApcPriorityWorldwideTrackedDdp]:
    ApcServiceCodeConstant.ApcPriorityWorldwideTrackedDdp,
  [ApcServices.ApcPriorityWorldwideExpedited]:
    ApcServiceCodeConstant.ApcPriorityWorldwideExpedited,
  [ApcServices.ApcPriorityWorldwideExpeditedDdp]:
    ApcServiceCodeConstant.ApcPriorityWorldwideExpeditedDdp,
};

enum FirstMileServiceCodeConstant {
  XParcelGround = "fm_a",
  XParcelPriority = "fm_b",
  XParcelExpedited = "fm_c",
  XParcelExpeditedPlus = "fm_d",
}
const firstMileServiceToServiceCodeConstant: Record<
  FirstMileServices,
  FirstMileServiceCodeConstant
> = {
  [FirstMileServices.XParcelGround]: FirstMileServiceCodeConstant.XParcelGround,
  [FirstMileServices.XParcelPriority]:
    FirstMileServiceCodeConstant.XParcelPriority,
  [FirstMileServices.XParcelExpedited]:
    FirstMileServiceCodeConstant.XParcelExpedited,
  [FirstMileServices.XParcelExpeditedPlus]:
    FirstMileServiceCodeConstant.XParcelExpeditedPlus,
};

enum DhlExpressServiceCodeConstant {
  BreakBulkEconomy = "dhl_a",
  BreakBulkExpress = "dhl_b",
  DomesticEconomySelect = "dhl_c",
  DomesticExpress = "dhl_d",
  DomesticExpress1030 = "dhl_e",
  DomesticExpress1200 = "dhl_f",
  EconomySelect = "dhl_g",
  EconomySelectNonDoc = "dhl_h",
  EuroPack = "dhl_i",
  EuropackNonDoc = "dhl_j",
  Express1030 = "dhl_k",
  Express1030NonDoc = "dhl_l",
  Express1200 = "dhl_m",
  Express1200NonDoc = "dhl_n",
  Express900 = "dhl_o",
  Express900NonDoc = "dhl_p",
  ExpressEasy = "dhl_q",
  ExpressEasyNonDoc = "dhl_r",
  ExpressEnvelope = "dhl_s",
  ExpressWorldwide = "dhl_t",
  ExpressWorldwideNonDoc = "dhl_u",
  ExpressWorldwide1030 = "dhl_v",
  ExpressWorldwide1030NonDoc = "dhl_w",
  ExpressWorldwide1200 = "dhl_x",
  ExpressWorldwide1200NonDoc = "dhl_y",
  ExpressWorldwide900 = "dhl_z",
  ExpressWorldwide900NonDoc = "dhl_aa",
  ExpressWorldwideEasy = "dhl_ab",
  ExpressWorldwideEasyNonDoc = "dhl_ac",
  ExpressWorldwideEnvelope = "dhl_ad",
  ExpressWorldwideEnvelopeNonDoc = "dhl_ae",
  ExpressWorldwide1030Economy = "dhl_af",
  ExpressWorldwide1030EconomyNonDoc = "dhl_ag",
  BreakBulkEconomyNonDoc = "dhl_ah",
  BreakBulkExpressNonDoc = "dhl_ai",
  DomesticEconomySelectNonDoc = "dhl_aj",
  DomesticExpressNonDoc = "dhl_ak",
  ExpressWorldwideB2C = "dhl_al",
  ExpressWorldwideB2CNonDoc = "dhl_am",
  ExpressWorldwideECX = "dhl_an",
  FreightWorldwide = "dhl_ao",
  GlobalmailBusiness = "dhl_ap",
  JetLine = "dhl_aq",
  JumboBox = "dhl_ar",
  LogisticsServices = "dhl_as",
  SameDay = "dhl_at",
  SecureLine = "dhl_au",
  SprintLine = "dhl_av",
  MedicalExpressNonDoc = "dhl_aw",
  MedicalExpress = "dhl_ax",
}
const dhlExpressServiceToServiceCodeConstant: Record<
  DhlExpressServices,
  DhlExpressServiceCodeConstant
> = {
  [DhlExpressServices.BreakBulkEconomy]:
    DhlExpressServiceCodeConstant.BreakBulkEconomy,
  [DhlExpressServices.BreakBulkExpress]:
    DhlExpressServiceCodeConstant.BreakBulkExpress,
  [DhlExpressServices.DomesticEconomySelect]:
    DhlExpressServiceCodeConstant.DomesticEconomySelect,
  [DhlExpressServices.DomesticExpress]:
    DhlExpressServiceCodeConstant.DomesticExpress,
  [DhlExpressServices.DomesticExpress1030]:
    DhlExpressServiceCodeConstant.DomesticExpress1030,
  [DhlExpressServices.DomesticExpress1200]:
    DhlExpressServiceCodeConstant.DomesticExpress1200,
  [DhlExpressServices.EconomySelect]:
    DhlExpressServiceCodeConstant.EconomySelect,
  [DhlExpressServices.EconomySelectNonDoc]:
    DhlExpressServiceCodeConstant.EconomySelectNonDoc,
  [DhlExpressServices.EuroPack]: DhlExpressServiceCodeConstant.EuroPack,
  [DhlExpressServices.EuropackNonDoc]:
    DhlExpressServiceCodeConstant.EuropackNonDoc,
  [DhlExpressServices.Express1030]: DhlExpressServiceCodeConstant.Express1030,
  [DhlExpressServices.Express1030NonDoc]:
    DhlExpressServiceCodeConstant.Express1030NonDoc,
  [DhlExpressServices.Express1200]: DhlExpressServiceCodeConstant.Express1200,
  [DhlExpressServices.Express1200NonDoc]:
    DhlExpressServiceCodeConstant.Express1200NonDoc,
  [DhlExpressServices.Express900]: DhlExpressServiceCodeConstant.Express900,
  [DhlExpressServices.Express900NonDoc]:
    DhlExpressServiceCodeConstant.Express900NonDoc,
  [DhlExpressServices.ExpressEasy]: DhlExpressServiceCodeConstant.ExpressEasy,
  [DhlExpressServices.ExpressEasyNonDoc]:
    DhlExpressServiceCodeConstant.ExpressEasyNonDoc,
  [DhlExpressServices.ExpressEnvelope]:
    DhlExpressServiceCodeConstant.ExpressEnvelope,
  [DhlExpressServices.ExpressWorldwide]:
    DhlExpressServiceCodeConstant.ExpressWorldwide,
  [DhlExpressServices.ExpressWorldwideNonDoc]:
    DhlExpressServiceCodeConstant.ExpressWorldwideNonDoc,
  [DhlExpressServices.ExpressWorldwideB2C]:
    DhlExpressServiceCodeConstant.ExpressWorldwideB2C,
  [DhlExpressServices.ExpressWorldwideB2CNonDoc]:
    DhlExpressServiceCodeConstant.ExpressWorldwideB2CNonDoc,
  [DhlExpressServices.ExpressWorldwideECX]:
    DhlExpressServiceCodeConstant.ExpressWorldwideECX,
  [DhlExpressServices.FreightWorldwide]:
    DhlExpressServiceCodeConstant.FreightWorldwide,
  [DhlExpressServices.GlobalmailBusiness]:
    DhlExpressServiceCodeConstant.GlobalmailBusiness,
  [DhlExpressServices.JetLine]: DhlExpressServiceCodeConstant.JetLine,
  [DhlExpressServices.JumboBox]: DhlExpressServiceCodeConstant.JumboBox,
  [DhlExpressServices.LogisticsServices]:
    DhlExpressServiceCodeConstant.LogisticsServices,
  [DhlExpressServices.SameDay]: DhlExpressServiceCodeConstant.SameDay,
  [DhlExpressServices.SecureLine]: DhlExpressServiceCodeConstant.SecureLine,
  [DhlExpressServices.SprintLine]: DhlExpressServiceCodeConstant.SprintLine,
  [DhlExpressServices.MedicalExpress]:
    DhlExpressServiceCodeConstant.MedicalExpress,
  [DhlExpressServices.MedicalExpressNonDoc]:
    DhlExpressServiceCodeConstant.MedicalExpressNonDoc,
};

enum DhlEcsServiceCodeConstant {
  DhlParcelExpedited = "dhl_ecs_a",
  DhlParcelExpeditedMax = "dhl_ecs_b",
  DhlParcelGround = "dhl_ecs_c",
  DhlParcelInternationalDirect = "dhl_ecs_d",
  DhlParcelInternationalStandard = "dhl_ecs_e",
  DhlPacketInternational = "dhl_ecs_f",
  DhlParcelInternationalDirectPriority = "dhl_ecs_g",
  DhlParcelInternationalDirectStandard = "dhl_ecs_h",
  DhlParcelInternationalDirectSMB = "dhl_ecs_i",
  DhlParcelInternationalStandardSMB = "dhl_ecs_j",
  DhlBPMExpedited = "dhl_ecs_k",
  DhlBPMGround = "dhl_ecs_l",
  DhlSmartMailParcelReturnGround = "dhl_ecs_m",
  DhlSmartMailParcelReturnPlus = "dhl_ecs_n",
  DhlSmartMailParcelReturnLight = "dhl_ecs_o",
}
const dhlEcsServiceToServiceCodeConstant: Record<
  DhlEcsServices,
  DhlEcsServiceCodeConstant
> = {
  [DhlEcsServices.DhlParcelExpedited]:
    DhlEcsServiceCodeConstant.DhlParcelExpedited,
  [DhlEcsServices.DhlParcelExpeditedMax]:
    DhlEcsServiceCodeConstant.DhlParcelExpeditedMax,
  [DhlEcsServices.DhlParcelGround]: DhlEcsServiceCodeConstant.DhlParcelGround,
  [DhlEcsServices.DhlParcelInternationalDirect]:
    DhlEcsServiceCodeConstant.DhlParcelInternationalDirect,
  [DhlEcsServices.DhlParcelInternationalStandard]:
    DhlEcsServiceCodeConstant.DhlParcelInternationalStandard,
  [DhlEcsServices.DhlPacketInternational]:
    DhlEcsServiceCodeConstant.DhlPacketInternational,
  [DhlEcsServices.DhlParcelInternationalDirectPriority]:
    DhlEcsServiceCodeConstant.DhlParcelInternationalDirectPriority,
  [DhlEcsServices.DhlParcelInternationalDirectStandard]:
    DhlEcsServiceCodeConstant.DhlParcelInternationalDirectStandard,
  [DhlEcsServices.DhlParcelInternationalDirectSMB]:
    DhlEcsServiceCodeConstant.DhlParcelInternationalDirectSMB,
  [DhlEcsServices.DhlParcelInternationalStandardSMB]:
    DhlEcsServiceCodeConstant.DhlParcelInternationalStandardSMB,
  [DhlEcsServices.DhlBPMExpedited]: DhlEcsServiceCodeConstant.DhlBPMExpedited,
  [DhlEcsServices.DhlBPMGround]: DhlEcsServiceCodeConstant.DhlBPMGround,
  [DhlEcsServices.DhlSmartMailParcelReturnGround]:
    DhlEcsServiceCodeConstant.DhlSmartMailParcelReturnGround,
  [DhlEcsServices.DhlSmartMailParcelReturnPlus]:
    DhlEcsServiceCodeConstant.DhlSmartMailParcelReturnPlus,
  [DhlEcsServices.DhlSmartMailParcelReturnLight]:
    DhlEcsServiceCodeConstant.DhlSmartMailParcelReturnLight,
};

enum UspsServiceCodeConstant {
  GroundAdvantageReturn = "usps_a",
  ExpressMailInternational = "usps_b",
  PriorityMailInternational = "usps_c",
  FirstClassPackageInternationalService = "usps_d",
  GroundAdvantage = "usps_e",
  PriorityMailReturn = "usps_f",
  Priority = "usps_g",
  Express = "usps_h",
  PriorityMailExpressReturn = "usps_i",
  MediaMail = "usps_j",
  LibraryMail = "usps_k",
  FirstClass = "usps_l",
  FirstClassMailInternational = "usps_m",
  GroundReturn = "usps_n",
  FirstClassPkgReturn = "usps_o",
}
const uspsServiceToServiceCodeConstant: Record<
  UspsServices,
  UspsServiceCodeConstant
> = {
  [UspsServices.GroundAdvantageReturn]:
    UspsServiceCodeConstant.GroundAdvantageReturn,
  [UspsServices.ExpressMailInternational]:
    UspsServiceCodeConstant.ExpressMailInternational,
  [UspsServices.FirstClassMailInternational]:
    UspsServiceCodeConstant.FirstClassMailInternational,
  [UspsServices.PriorityMailInternational]:
    UspsServiceCodeConstant.PriorityMailInternational,
  [UspsServices.FirstClassPackageInternationalService]:
    UspsServiceCodeConstant.FirstClassPackageInternationalService,
  [UspsServices.GroundAdvantage]: UspsServiceCodeConstant.GroundAdvantage,
  [UspsServices.PriorityMailReturn]: UspsServiceCodeConstant.PriorityMailReturn,
  [UspsServices.Priority]: UspsServiceCodeConstant.Priority,
  [UspsServices.Express]: UspsServiceCodeConstant.Express,
  [UspsServices.PriorityMailExpressReturn]:
    UspsServiceCodeConstant.PriorityMailExpressReturn,
  [UspsServices.FirstClass]: UspsServiceCodeConstant.FirstClass,
  [UspsServices.MediaMail]: UspsServiceCodeConstant.MediaMail,
  [UspsServices.LibraryMail]: UspsServiceCodeConstant.LibraryMail,
  [UspsServices.GroundReturn]: UspsServiceCodeConstant.GroundReturn,
  [UspsServices.FirstClassPkgReturn]:
    UspsServiceCodeConstant.FirstClassPkgReturn,
};

enum AustraliaPostServiceCodeConstant {
  ParcelPost = "ap_a",
  ExpressPost = "ap_b",
  International = "ap_c",
  Commercial = "ap_d",
  ParcelPostSignature = "ap_e",
  ExpressPostSignature = "ap_f",
  InternationalSignature = "ap_g",
  CommercialSignature = "ap_h",
  EParcel1 = "ap_i",
  EParcelPostReturns = "ap_j",
  ExpressParcelPostReturns = "ap_k",
  ExpressPostEParcel = "ap_l",
  InternationalEconomyAirmailParcels = "ap_m",
  InternationalEconomyWSODREGPost = "ap_n",
  InternationalExpressDocSecIDocs = "ap_o",
  InternationalExpressMerchandise = "ap_p",
  InternationalExpressMerchECIMerch = "ap_q",
  InternationalStandardPackTrack = "ap_r",
  ParcelPostExtra = "ap_s",
  ParcelPostWinePlusSignature = "ap_t",
  ParcelPostWineSignature = "ap_u",
  ExpressEparcelPostReturns = "ap_v",
  EparcelPostReturns = "ap_w",
  IntlExpressDocs = "ap_x",
  IntlStandardPackTrack = "ap_y",
  IntLStandardWithSignature = "ap_z",
  IntlExpressMerch = "ap_aa",
}

enum CanadaPostServiceCodeConstant {
  RegularParcel = "cp_a",
  ExpeditedParcel = "cp_b",
  Xpresspost = "cp_c",
  Priority = "cp_d",
  ExpeditedParcelUSA = "cp_e",
  SmallPacketUSAAir = "cp_f",
  TrackedPacketUSA = "cp_g",
  TrackedPacketUSALVM = "cp_h",
  XpresspostUSA = "cp_i",
  XpresspostInternational = "cp_j",
  InternationalParcelAir = "cp_k",
  InternationalParcelSurface = "cp_l",
  SmallPacketInternationalAir = "cp_m",
  SmallPacketInternationalSurface = "cp_n",
  TrackedPacketInternational = "cp_o",
  ExpeditedParcelPlus = "cp_p",
}
const australiaPostServiceToServiceCodeConstant: Record<
  AustraliaPostServices,
  AustraliaPostServiceCodeConstant
> = {
  [AustraliaPostServices.ParcelPost]:
    AustraliaPostServiceCodeConstant.ParcelPost,
  [AustraliaPostServices.ExpressPost]:
    AustraliaPostServiceCodeConstant.ExpressPost,
  [AustraliaPostServices.International]:
    AustraliaPostServiceCodeConstant.International,
  [AustraliaPostServices.Commercial]:
    AustraliaPostServiceCodeConstant.Commercial,
  [AustraliaPostServices.ParcelPostSignature]:
    AustraliaPostServiceCodeConstant.ParcelPost,
  [AustraliaPostServices.ExpressPostSignature]:
    AustraliaPostServiceCodeConstant.ExpressPost,
  [AustraliaPostServices.InternationalSignature]:
    AustraliaPostServiceCodeConstant.International,
  [AustraliaPostServices.CommercialSignature]:
    AustraliaPostServiceCodeConstant.Commercial,
  [AustraliaPostServices.EParcel1]: AustraliaPostServiceCodeConstant.EParcel1,
  [AustraliaPostServices.EParcelPostReturns]:
    AustraliaPostServiceCodeConstant.EParcelPostReturns,
  [AustraliaPostServices.ExpressParcelPostReturns]:
    AustraliaPostServiceCodeConstant.ExpressParcelPostReturns,
  [AustraliaPostServices.ExpressPostEParcel]:
    AustraliaPostServiceCodeConstant.ExpressPostEParcel,
  [AustraliaPostServices.InternationalEconomyAirmailParcels]:
    AustraliaPostServiceCodeConstant.InternationalEconomyAirmailParcels,
  [AustraliaPostServices.InternationalEconomyWSODREGPost]:
    AustraliaPostServiceCodeConstant.InternationalEconomyWSODREGPost,
  [AustraliaPostServices.InternationalExpressDocSecIDocs]:
    AustraliaPostServiceCodeConstant.InternationalExpressDocSecIDocs,
  [AustraliaPostServices.InternationalExpressMerchandise]:
    AustraliaPostServiceCodeConstant.InternationalExpressMerchandise,
  [AustraliaPostServices.InternationalExpressMerchECIMerch]:
    AustraliaPostServiceCodeConstant.InternationalExpressMerchECIMerch,
  [AustraliaPostServices.InternationalStandardPackTrack]:
    AustraliaPostServiceCodeConstant.InternationalStandardPackTrack,
  [AustraliaPostServices.ParcelPostExtra]:
    AustraliaPostServiceCodeConstant.ParcelPostExtra,
  [AustraliaPostServices.ParcelPostWinePlusSignature]:
    AustraliaPostServiceCodeConstant.ParcelPostWinePlusSignature,
  [AustraliaPostServices.ParcelPostWineSignature]:
    AustraliaPostServiceCodeConstant.ParcelPostWineSignature,
  [AustraliaPostServices.ExpressEparcelPostReturns]:
    AustraliaPostServiceCodeConstant.ExpressEparcelPostReturns,
  [AustraliaPostServices.EparcelPostReturns]:
    AustraliaPostServiceCodeConstant.EparcelPostReturns,
  [AustraliaPostServices.IntlExpressDocs]:
    AustraliaPostServiceCodeConstant.IntlExpressDocs,
  [AustraliaPostServices.IntlStandardPackTrack]:
    AustraliaPostServiceCodeConstant.IntlStandardPackTrack,
  [AustraliaPostServices.IntLStandardWithSignature]:
    AustraliaPostServiceCodeConstant.IntLStandardWithSignature,
  [AustraliaPostServices.IntlExpressMerch]:
    AustraliaPostServiceCodeConstant.IntlExpressMerch,
};

const canadaPostServiceToServiceCodeConstant: Record<
  CanadaPostServices,
  CanadaPostServiceCodeConstant
> = {
  [CanadaPostServices.RegularParcel]:
    CanadaPostServiceCodeConstant.RegularParcel,
  [CanadaPostServices.ExpeditedParcel]:
    CanadaPostServiceCodeConstant.ExpeditedParcel,
  [CanadaPostServices.Xpresspost]: CanadaPostServiceCodeConstant.Xpresspost,
  [CanadaPostServices.Priority]: CanadaPostServiceCodeConstant.Priority,
  [CanadaPostServices.ExpeditedParcelUSA]:
    CanadaPostServiceCodeConstant.ExpeditedParcelUSA,
  [CanadaPostServices.SmallPacketUSAAir]:
    CanadaPostServiceCodeConstant.SmallPacketUSAAir,
  [CanadaPostServices.TrackedPacketUSA]:
    CanadaPostServiceCodeConstant.TrackedPacketUSA,
  [CanadaPostServices.TrackedPacketUSALVM]:
    CanadaPostServiceCodeConstant.TrackedPacketUSALVM,
  [CanadaPostServices.XpresspostUSA]:
    CanadaPostServiceCodeConstant.XpresspostUSA,
  [CanadaPostServices.XpresspostInternational]:
    CanadaPostServiceCodeConstant.XpresspostInternational,
  [CanadaPostServices.InternationalParcelAir]:
    CanadaPostServiceCodeConstant.InternationalParcelAir,
  [CanadaPostServices.InternationalParcelSurface]:
    CanadaPostServiceCodeConstant.InternationalParcelSurface,
  [CanadaPostServices.SmallPacketInternationalAir]:
    CanadaPostServiceCodeConstant.SmallPacketInternationalAir,
  [CanadaPostServices.SmallPacketInternationalSurface]:
    CanadaPostServiceCodeConstant.SmallPacketInternationalSurface,
  [CanadaPostServices.TrackedPacketInternational]:
    CanadaPostServiceCodeConstant.TrackedPacketInternational,
  [CanadaPostServices.ExpeditedParcelPlus]:
    CanadaPostServiceCodeConstant.ExpeditedParcelPlus,
};

enum GlsServiceCodeConstant {
  EarlyPriorityOvernight = "gls_a",
  PriorityOvernight = "gls_b",
  CaliforniaParcelService = "gls_c",
  SaturdayDeliveryService = "gls_d",
  EarlySaturdayService = "gls_e",
  NoonPriorityService = "gls_f",
}

const glsServiceToServiceCodeConstant: Record<
  GlsServices,
  GlsServiceCodeConstant
> = {
  [GlsServices.EarlyPriorityOvernight]:
    GlsServiceCodeConstant.EarlyPriorityOvernight,
  [GlsServices.PriorityOvernight]: GlsServiceCodeConstant.PriorityOvernight,
  [GlsServices.CaliforniaParcelService]:
    GlsServiceCodeConstant.CaliforniaParcelService,
  [GlsServices.SaturdayDeliveryService]:
    GlsServiceCodeConstant.SaturdayDeliveryService,
  [GlsServices.EarlySaturdayService]:
    GlsServiceCodeConstant.EarlySaturdayService,
  [GlsServices.NoonPriorityService]: GlsServiceCodeConstant.NoonPriorityService,
};

export enum AsendiaServiceCodeConstant {
  ADS = "a_a",
  AirFreightInbound = "a_b",
  AirFreightOutbound = "a_c",
  AsendiaDomesticBoundPrinterMatterExpedited = "a_d",
  AsendiaDomesticBoundPrinterMatterGround = "a_e",
  AsendiaDomesticFlatsExpedited = "a_f",
  AsendiaDomesticFlatsGround = "a_g",
  AsendiaDomesticParcelGroundOver1lb = "a_h",
  AsendiaDomesticParcelGroundUnder1lb = "a_i",
  AsendiaDomesticParcelMAXOver1lb = "a_j",
  AsendiaDomesticParcelMAXUnder1lb = "a_k",
  AsendiaDomesticParcelOver1lbExpedited = "a_l",
  AsendiaDomesticParcelUnder1lbExpedited = "a_m",
  AsendiaDomesticPromoParcelExpedited = "a_n",
  AsendiaDomesticPromoParcelGround = "a_o",
  BulkFreight = "a_p",
  BusinessMailCanadaLettermail = "a_q",
  BusinessMailCanadaLettermailMachineable = "a_r",
  BusinessMailEconomy = "a_s",
  BusinessMailEconomyLPWholesale = "a_t",
  BusinessMailEconomySPWholesale = "a_u",
  BusinessMailIPA = "a_v",
  BusinessMailISAL = "a_w",
  BusinessMailPriority = "a_x",
  BusinessMailPriorityLPWholesale = "a_y",
  BusinessMailPrioritySPWholesale = "a_z",
  MarketingMailCanadaPersonalizedLCP = "a_aa",
  MarketingMailCanadaPersonalizedMachineable = "a_ab",
  MarketingMailCanadaPersonalizedNDG = "a_ac",
  MarketingMailEconomy = "a_ad",
  MarketingMailIPA = "a_ae",
  MarketingMailISAL = "a_af",
  MarketingMailPriority = "a_ag",
  PublicationsCanadaLCP = "a_ah",
  PublicationsCanadaNDG = "a_ai",
  PublicationsEconomy = "a_aj",
  PublicationsIPA = "a_ak",
  PublicationsISAL = "a_al",
  PublicationsPriority = "a_am",
  ePAQElite = "a_an",
  ePAQEliteCustom = "a_ao",
  ePAQEliteDAP = "a_ap",
  ePAQEliteDDP = "a_aq",
  ePAQEliteDDPOversized = "a_ar",
  ePAQEliteDPD = "a_as",
  ePAQEliteDirectAccessCanadaDDP = "a_at",
  ePAQEliteOversized = "a_au",
  ePAQPlus = "a_av",
  ePAQPlusCustom = "a_aw",
  ePAQPlusCustomsPrepaid = "a_ax",
  ePAQPlusDAP = "a_ay",
  ePAQPlusDDP = "a_az",
  ePAQPlusEconomy = "a_ba",
  ePAQPlusWholesale = "a_bb",
  ePAQPlusePacket = "a_bc",
  ePAQPlusePacketCanadaCustomsPrePaid = "a_bd",
  ePAQPlusePacketCanadaDDP = "a_be",
  ePAQReturnsDomestic = "a_bf",
  ePAQReturnsInternational = "a_bg",
  ePAQSelect = "a_bh",
  ePAQSelectCustom = "a_bi",
  ePAQSelectCustomsPrepaidByShopper = "a_bj",
  ePAQSelectDAP = "a_bk",
  ePAQSelectDDP = "a_bl",
  ePAQSelectDDPDirectAccess = "a_bm",
  ePAQSelectDirectAccess = "a_bn",
  ePAQSelectDirectAccessCanadaDDP = "a_bo",
  ePAQSelectEconomy = "a_bp",
  ePAQSelectOversized = "a_bq",
  ePAQSelectOversizedDDP = "a_br",
  ePAQSelectPMEI = "a_bs",
  ePAQSelectPMEICanadaCustomsPrePaid = "a_bt",
  ePAQSelectPMEIPCPostage = "a_bu",
  ePAQSelectPMI = "a_bv",
  ePAQSelectPMICanadaCustomsPrepaid = "a_bw",
  ePAQSelectPMICanadaDDP = "a_bx",
  ePAQSelectPMINonPresort = "a_by",
  ePAQSelectPMIPCPostage = "a_bz",
  ePAQStandard = "a_ca",
  ePAQStandardCustom = "a_cb",
  ePAQStandardEconomy = "a_cc",
  ePAQStandardIPA = "a_cd",
  ePAQStandardISAL = "a_ce",
  ePaqSelectPMEINonPresort = "a_cf",
  ePAQSelectDDPDG = "a_cg",
  AsendiaDomesticParcelMaxUnder70lb = "a_ch",
}

const asendiaServiceToServiceCodeConstant: Record<
  AsendiaServices,
  AsendiaServiceCodeConstant
> = {
  [AsendiaServices.ADS]: AsendiaServiceCodeConstant.ADS,
  [AsendiaServices.AirFreightInbound]:
    AsendiaServiceCodeConstant.AirFreightInbound,
  [AsendiaServices.AirFreightOutbound]:
    AsendiaServiceCodeConstant.AirFreightOutbound,
  [AsendiaServices.AsendiaDomesticBoundPrinterMatterExpedited]:
    AsendiaServiceCodeConstant.AsendiaDomesticBoundPrinterMatterExpedited,
  [AsendiaServices.AsendiaDomesticBoundPrinterMatterGround]:
    AsendiaServiceCodeConstant.AsendiaDomesticBoundPrinterMatterGround,
  [AsendiaServices.AsendiaDomesticFlatsExpedited]:
    AsendiaServiceCodeConstant.AsendiaDomesticFlatsExpedited,
  [AsendiaServices.AsendiaDomesticFlatsGround]:
    AsendiaServiceCodeConstant.AsendiaDomesticFlatsGround,
  [AsendiaServices.AsendiaDomesticParcelGroundOver1lb]:
    AsendiaServiceCodeConstant.AsendiaDomesticParcelGroundOver1lb,
  [AsendiaServices.AsendiaDomesticParcelGroundUnder1lb]:
    AsendiaServiceCodeConstant.AsendiaDomesticParcelGroundUnder1lb,
  [AsendiaServices.AsendiaDomesticParcelMAXOver1lb]:
    AsendiaServiceCodeConstant.AsendiaDomesticParcelMAXOver1lb,
  [AsendiaServices.AsendiaDomesticParcelMAXUnder1lb]:
    AsendiaServiceCodeConstant.AsendiaDomesticParcelMAXUnder1lb,
  [AsendiaServices.AsendiaDomesticParcelOver1lbExpedited]:
    AsendiaServiceCodeConstant.AsendiaDomesticParcelOver1lbExpedited,
  [AsendiaServices.AsendiaDomesticParcelUnder1lbExpedited]:
    AsendiaServiceCodeConstant.AsendiaDomesticParcelUnder1lbExpedited,
  [AsendiaServices.AsendiaDomesticPromoParcelExpedited]:
    AsendiaServiceCodeConstant.AsendiaDomesticPromoParcelExpedited,
  [AsendiaServices.AsendiaDomesticPromoParcelGround]:
    AsendiaServiceCodeConstant.AsendiaDomesticPromoParcelGround,
  [AsendiaServices.BulkFreight]: AsendiaServiceCodeConstant.BulkFreight,
  [AsendiaServices.BusinessMailCanadaLettermail]:
    AsendiaServiceCodeConstant.BusinessMailCanadaLettermail,
  [AsendiaServices.BusinessMailCanadaLettermailMachineable]:
    AsendiaServiceCodeConstant.BusinessMailCanadaLettermailMachineable,
  [AsendiaServices.BusinessMailEconomy]:
    AsendiaServiceCodeConstant.BusinessMailEconomy,
  [AsendiaServices.BusinessMailEconomyLPWholesale]:
    AsendiaServiceCodeConstant.BusinessMailEconomyLPWholesale,
  [AsendiaServices.BusinessMailEconomySPWholesale]:
    AsendiaServiceCodeConstant.BusinessMailEconomySPWholesale,
  [AsendiaServices.BusinessMailIPA]: AsendiaServiceCodeConstant.BusinessMailIPA,
  [AsendiaServices.BusinessMailISAL]:
    AsendiaServiceCodeConstant.BusinessMailISAL,
  [AsendiaServices.BusinessMailPriority]:
    AsendiaServiceCodeConstant.BusinessMailPriority,
  [AsendiaServices.BusinessMailPriorityLPWholesale]:
    AsendiaServiceCodeConstant.BusinessMailPriorityLPWholesale,
  [AsendiaServices.BusinessMailPrioritySPWholesale]:
    AsendiaServiceCodeConstant.BusinessMailPrioritySPWholesale,
  [AsendiaServices.MarketingMailCanadaPersonalizedLCP]:
    AsendiaServiceCodeConstant.MarketingMailCanadaPersonalizedLCP,
  [AsendiaServices.MarketingMailCanadaPersonalizedMachineable]:
    AsendiaServiceCodeConstant.MarketingMailCanadaPersonalizedMachineable,
  [AsendiaServices.MarketingMailCanadaPersonalizedNDG]:
    AsendiaServiceCodeConstant.MarketingMailCanadaPersonalizedNDG,
  [AsendiaServices.MarketingMailEconomy]:
    AsendiaServiceCodeConstant.MarketingMailEconomy,
  [AsendiaServices.MarketingMailIPA]:
    AsendiaServiceCodeConstant.MarketingMailIPA,
  [AsendiaServices.MarketingMailISAL]:
    AsendiaServiceCodeConstant.MarketingMailISAL,
  [AsendiaServices.MarketingMailPriority]:
    AsendiaServiceCodeConstant.MarketingMailPriority,
  [AsendiaServices.PublicationsCanadaLCP]:
    AsendiaServiceCodeConstant.PublicationsCanadaLCP,
  [AsendiaServices.PublicationsCanadaNDG]:
    AsendiaServiceCodeConstant.PublicationsCanadaNDG,
  [AsendiaServices.PublicationsEconomy]:
    AsendiaServiceCodeConstant.PublicationsEconomy,
  [AsendiaServices.PublicationsIPA]: AsendiaServiceCodeConstant.PublicationsIPA,
  [AsendiaServices.PublicationsISAL]:
    AsendiaServiceCodeConstant.PublicationsISAL,
  [AsendiaServices.PublicationsPriority]:
    AsendiaServiceCodeConstant.PublicationsPriority,
  [AsendiaServices.ePAQElite]: AsendiaServiceCodeConstant.ePAQElite,
  [AsendiaServices.ePAQEliteCustom]: AsendiaServiceCodeConstant.ePAQEliteCustom,
  [AsendiaServices.ePAQEliteDAP]: AsendiaServiceCodeConstant.ePAQEliteDAP,
  [AsendiaServices.ePAQEliteDDP]: AsendiaServiceCodeConstant.ePAQEliteDDP,
  [AsendiaServices.ePAQEliteDDPOversized]:
    AsendiaServiceCodeConstant.ePAQEliteDDPOversized,
  [AsendiaServices.ePAQEliteDPD]: AsendiaServiceCodeConstant.ePAQEliteDPD,
  [AsendiaServices.ePAQEliteDirectAccessCanadaDDP]:
    AsendiaServiceCodeConstant.ePAQEliteDirectAccessCanadaDDP,
  [AsendiaServices.ePAQEliteOversized]:
    AsendiaServiceCodeConstant.ePAQEliteOversized,
  [AsendiaServices.ePAQPlus]: AsendiaServiceCodeConstant.ePAQPlus,
  [AsendiaServices.ePAQPlusCustom]: AsendiaServiceCodeConstant.ePAQPlusCustom,
  [AsendiaServices.ePAQPlusCustomsPrepaid]:
    AsendiaServiceCodeConstant.ePAQPlusCustomsPrepaid,
  [AsendiaServices.ePAQPlusDAP]: AsendiaServiceCodeConstant.ePAQPlusDAP,
  [AsendiaServices.ePAQPlusDDP]: AsendiaServiceCodeConstant.ePAQPlusDDP,
  [AsendiaServices.ePAQPlusEconomy]: AsendiaServiceCodeConstant.ePAQPlusEconomy,
  [AsendiaServices.ePAQPlusWholesale]:
    AsendiaServiceCodeConstant.ePAQPlusWholesale,
  [AsendiaServices.ePAQPlusePacket]: AsendiaServiceCodeConstant.ePAQPlusePacket,
  [AsendiaServices.ePAQPlusePacketCanadaCustomsPrePaid]:
    AsendiaServiceCodeConstant.ePAQPlusePacketCanadaCustomsPrePaid,
  [AsendiaServices.ePAQPlusePacketCanadaDDP]:
    AsendiaServiceCodeConstant.ePAQPlusePacketCanadaDDP,
  [AsendiaServices.ePAQReturnsDomestic]:
    AsendiaServiceCodeConstant.ePAQReturnsDomestic,
  [AsendiaServices.ePAQReturnsInternational]:
    AsendiaServiceCodeConstant.ePAQReturnsInternational,
  [AsendiaServices.ePAQSelect]: AsendiaServiceCodeConstant.ePAQSelect,
  [AsendiaServices.ePAQSelectCustom]:
    AsendiaServiceCodeConstant.ePAQSelectCustom,
  [AsendiaServices.ePAQSelectCustomsPrepaidByShopper]:
    AsendiaServiceCodeConstant.ePAQSelectCustomsPrepaidByShopper,
  [AsendiaServices.ePAQSelectDAP]: AsendiaServiceCodeConstant.ePAQSelectDAP,
  [AsendiaServices.ePAQSelectDDP]: AsendiaServiceCodeConstant.ePAQSelectDDP,
  [AsendiaServices.ePAQSelectDDPDirectAccess]:
    AsendiaServiceCodeConstant.ePAQSelectDDPDirectAccess,
  [AsendiaServices.ePAQSelectDirectAccess]:
    AsendiaServiceCodeConstant.ePAQSelectDirectAccess,
  [AsendiaServices.ePAQSelectDirectAccessCanadaDDP]:
    AsendiaServiceCodeConstant.ePAQSelectDirectAccessCanadaDDP,
  [AsendiaServices.ePAQSelectEconomy]:
    AsendiaServiceCodeConstant.ePAQSelectEconomy,
  [AsendiaServices.ePAQSelectOversized]:
    AsendiaServiceCodeConstant.ePAQSelectOversized,
  [AsendiaServices.ePAQSelectOversizedDDP]:
    AsendiaServiceCodeConstant.ePAQSelectOversizedDDP,
  [AsendiaServices.ePAQSelectPMEI]: AsendiaServiceCodeConstant.ePAQSelectPMEI,
  [AsendiaServices.ePAQSelectPMEICanadaCustomsPrePaid]:
    AsendiaServiceCodeConstant.ePAQSelectPMEICanadaCustomsPrePaid,
  [AsendiaServices.ePAQSelectPMEIPCPostage]:
    AsendiaServiceCodeConstant.ePAQSelectPMEIPCPostage,
  [AsendiaServices.ePAQSelectPMI]: AsendiaServiceCodeConstant.ePAQSelectPMI,
  [AsendiaServices.ePAQSelectPMICanadaCustomsPrepaid]:
    AsendiaServiceCodeConstant.ePAQSelectPMICanadaCustomsPrepaid,
  [AsendiaServices.ePAQSelectPMICanadaDDP]:
    AsendiaServiceCodeConstant.ePAQSelectPMICanadaDDP,
  [AsendiaServices.ePAQSelectPMINonPresort]:
    AsendiaServiceCodeConstant.ePAQSelectPMINonPresort,
  [AsendiaServices.ePAQSelectPMIPCPostage]:
    AsendiaServiceCodeConstant.ePAQSelectPMIPCPostage,
  [AsendiaServices.ePAQStandard]: AsendiaServiceCodeConstant.ePAQStandard,
  [AsendiaServices.ePAQStandardCustom]:
    AsendiaServiceCodeConstant.ePAQStandardCustom,
  [AsendiaServices.ePAQStandardEconomy]:
    AsendiaServiceCodeConstant.ePAQStandardEconomy,
  [AsendiaServices.ePAQStandardIPA]: AsendiaServiceCodeConstant.ePAQStandardIPA,
  [AsendiaServices.ePAQStandardISAL]:
    AsendiaServiceCodeConstant.ePAQStandardISAL,
  [AsendiaServices.ePaqSelectPMEINonPresort]:
    AsendiaServiceCodeConstant.ePaqSelectPMEINonPresort,
  [AsendiaServices.ePAQSelectDDPDG]: AsendiaServiceCodeConstant.ePAQSelectDDPDG,
  [AsendiaServices.AsendiaDomesticParcelMaxUnder70lb]:
    AsendiaServiceCodeConstant.AsendiaDomesticParcelMaxUnder70lb,
};

enum RoyalMailV3ServiceCodeConstant {
  RoyalMailV3 = "rmv3_a",
  DEImportRoyalMail24PlusParcelsWeekendService = "rmv3_b",
  DEImportStandard24LargeLetter = "rmv3_c",
  DEImportStandard24LargeLetterWeekendService = "rmv3_d",
  DEImportStandard24Parcel = "rmv3_e",
  DEImportStandard24ParcelWeekendService = "rmv3_f",
  DEImportStandard48LargeLetter = "rmv3_g",
  DEImportStandard48LargeLetterWeekendService = "rmv3_h",
  DEImportTracked24ParcelBoxableHighVolumeWeekendService = "rmv3_i",
  DEImportTracked24ParcelBoxableWeekendService = "rmv3_j",
  DEImportTracked24ParcelHighVolumeWeekendService = "rmv3_k",
  DEImportTracked24SignatureParcelHighVolumeWeekendServiceAGE = "rmv3_l",
  DEImportTracked48ParcelBoxableHighVolumeWeekendService = "rmv3_m",
  DEImportTracked48ParcelBoxableWeekendService = "rmv3_n",
  DEImportTracked48ParcelHighVolumeWeekendService = "rmv3_o",
  DEImportTracked48SignatureParcelHighVolumeAGE = "rmv3_p",
  DEImportTracked48SignatureParcelHighVolumeWeekendServiceAGE = "rmv3_q",
  DEImporttoEUMaxSortLargeLetter = "rmv3_r",
  DEImporttoEUMaxSortParcel = "rmv3_s",
  DEImporttoEUSignedParcel = "rmv3_t",
  DEImporttoEUTrackedHighVolLargeLetter = "rmv3_u",
  DEImporttoEUTrackedParcel = "rmv3_v",
  DEImporttoEUTrackedSignedLargeLetter = "rmv3_w",
  DEImporttoEUTrackedSignedParcel = "rmv3_x",
  HMForcesMail = "rmv3_y",
  HMForcesSignedFor = "rmv3_z",
  HMForcesSpecialDelivery1000 = "rmv3_aa",
  HMForcesSpecialDelivery2500 = "rmv3_ab",
  HMForcesSpecialDelivery500 = "rmv3_ac",
  ImportDETracked24 = "rmv3_ad",
  ImportDETracked24HighVolume = "rmv3_ae",
  ImportDETracked24Letterboxable = "rmv3_af",
  ImportDETracked24LetterboxableHighVolume = "rmv3_ag",
  ImportDETracked24SignatureHighVolumeAGE = "rmv3_ah",
  ImportDETracked24SignatureStandardAGE = "rmv3_ai",
  ImportDETracked48 = "rmv3_aj",
  ImportDETracked48HighVolume = "rmv3_ak",
  ImportDETracked48Letterboxable = "rmv3_al",
  ImportDETracked48LetterboxableHighVolume = "rmv3_am",
  ImportDETrackedReturns24 = "rmv3_an",
  ImportDETrackedReturns48 = "rmv3_ao",
  InternationalBusinessMailLargeLetterCountryPricedPriority = "rmv3_ap",
  InternationalBusinessMailLargeLetterCountryPricedPriorityETOE = "rmv3_aq",
  InternationalBusinessMailLargeLetterCountrySortPriority = "rmv3_ar",
  InternationalBusinessMailLargeLetterMaxSortPriorityService = "rmv3_as",
  InternationalBusinessMailLargeLetterMaxSortResidueStandard = "rmv3_at",
  InternationalBusinessMailLargeLetterMaxSortStandardService = "rmv3_au",
  InternationalBusinessMailLargeLetterSemiTracked = "rmv3_av",
  InternationalBusinessMailLargeLetterZeroSortPriority = "rmv3_aw",
  InternationalBusinessMailLargeLetterZeroSortPriorityMachine = "rmv3_ax",
  InternationalBusinessMailLargeLetterZoneSortPriority = "rmv3_ay",
  InternationalBusinessMailLargeLetterZoneSortPriorityMachine = "rmv3_az",
  InternationalBusinessMailLetterMaxSortResidueStandard = "rmv3_ba",
  InternationalBusinessMailLettersMaxSortStandard = "rmv3_bb",
  InternationalBusinessMailLettersZeroSortPriority = "rmv3_bc",
  InternationalBusinessMailLettersZoneSortPriority = "rmv3_bd",
  InternationalBusinessMailSignedCountryPriced = "rmv3_be",
  InternationalBusinessMailSignedExtraCompensationCountryPriced = "rmv3_bf",
  InternationalBusinessMailSignedHighVolCountryPriced = "rmv3_bg",
  InternationalBusinessMailSignedZoneSort = "rmv3_bh",
  InternationalBusinessMailTrackedCountryPriced = "rmv3_bi",
  InternationalBusinessMailTrackedHighVolCountryPriced = "rmv3_bj",
  InternationalBusinessMailTrackedHighVolExtraCompCountryPriced = "rmv3_bk",
  InternationalBusinessMailTrackedLargeLetterCountryPricedETOE = "rmv3_bl",
  InternationalBusinessMailTrackedSignedCountryPriced = "rmv3_bm",
  InternationalBusinessMailTrackedSignedHighVolCountryPriced = "rmv3_bn",
  InternationalBusinessMailTrackedSignedHighVolExtraCompCountryPriced = "rmv3_bo",
  InternationalBusinessMailTrackedSignedLargeLetterCountryPricedETOE = "rmv3_bp",
  InternationalBusinessMailTrackedSignedZoneSort = "rmv3_bq",
  InternationalBusinessMailTrackedZoneSort = "rmv3_br",
  InternationalBusinessPCTrackedSignedLargeLetterHVExtraCompCountryPricedETOE = "rmv3_bs",
  InternationalBusinessParcelPriorityCountryPricedBoxable = "rmv3_bt",
  InternationalBusinessParcelPriorityCountryPricedBoxableDDP = "rmv3_bu",
  InternationalBusinessParcelTrackedCountryPriced = "rmv3_bv",
  InternationalBusinessParcelTrackedCountryPricedBoxable = "rmv3_bw",
  InternationalBusinessParcelTrackedCountryPricedBoxableDDP = "rmv3_bx",
  InternationalBusinessParcelTrackedCountryPricedBoxableExtraComp = "rmv3_by",
  InternationalBusinessParcels = "rmv3_bz",
  InternationalBusinessParcelsPrintDirectPriority = "rmv3_ca",
  InternationalBusinessParcelsPrintDirectStandard = "rmv3_cb",
  InternationalBusinessParcelsSignedCountryPriced = "rmv3_cc",
  InternationalBusinessParcelsSignedExtraCompensationCountryPriced = "rmv3_cd",
  InternationalBusinessParcelsSignedExtraCompensationZoneSort = "rmv3_ce",
  InternationalBusinessParcelsSignedZoneSort = "rmv3_cf",
  InternationalBusinessParcelsTrackedCountryPriced = "rmv3_cg",
  InternationalBusinessParcelsTrackedCountryPricedETOE = "rmv3_ch",
  InternationalBusinessParcelsTrackedDirectIrelandCountry = "rmv3_ci",
  InternationalBusinessParcelsTrackedExtraCompCountryPriced = "rmv3_cj",
  InternationalBusinessParcelsTrackedExtraCompZoneSort = "rmv3_ck",
  InternationalBusinessParcelsTrackedSignedCountryPriced = "rmv3_cl",
  InternationalBusinessParcelsTrackedSignedCountryPricedETOE = "rmv3_cm",
  InternationalBusinessParcelsTrackedSignedDDP = "rmv3_cn",
  InternationalBusinessParcelsTrackedSignedExtraCompensationCountryPriced = "rmv3_co",
  InternationalBusinessParcelsTrackedSignedExtraCompensationZoneSort = "rmv3_cp",
  InternationalBusinessParcelsTrackedSignedZoneSort = "rmv3_cq",
  InternationalBusinessParcelsTrackedZoneSort = "rmv3_cr",
  InternationalBusinessParcelsZeroSortPriority = "rmv3_cs",
  InternationalBusinessParcelsZeroSortPriorityETOE = "rmv3_ct",
  InternationalBusinessParcelsZoneSortPriorityService = "rmv3_cu",
  InternationalBusinessPersonalCorrespondenceLargeLetterCountrySortPriorityETOE = "rmv3_cv",
  InternationalBusinessPersonalCorrespondenceLetterPriorityUntracked = "rmv3_cw",
  InternationalBusinessPersonalCorrespondenceLetterPriorityUntrackedETOE = "rmv3_cx",
  InternationalBusinessPersonalCorrespondenceLetterTrackedHighVolCountryPricedETOE = "rmv3_cy",
  InternationalBusinessPersonalCorrespondenceLetterTrackedSignedHighVolCountryPricedETOE = "rmv3_cz",
  InternationalBusinessPersonalCorrespondenceMaxSortLetter = "rmv3_da",
  InternationalBusinessPersonalCorrespondenceMaxSortResidueLargeLetter = "rmv3_db",
  InternationalBusinessPersonalCorrespondenceMaxSortResidueLetter = "rmv3_dc",
  InternationalBusinessPersonalCorrespondenceSignedLargeLetterExtraCompensationCountryPricedETOE = "rmv3_dd",
  InternationalBusinessPersonalCorrespondenceSignedLetterHighVolCountryPricedETOE = "rmv3_de",
  InternationalBusinessPersonalCorrespondenceTrackedLargeLetterHighVolExtraCompCountryPricedETOE = "rmv3_df",
  InternationalBusinessPrintedMatterPacket = "rmv3_dg",
  InternationalBusinessTrackedExpressNPC = "rmv3_dh",
  InternationalEconomyOnAccount = "rmv3_di",
  InternationalSignedOnAccount = "rmv3_dj",
  InternationalSignedOnAccountExtraComp = "rmv3_dk",
  InternationalStandardOnAccount = "rmv3_dl",
  InternationalTrackedOnAccount = "rmv3_dm",
  InternationalTrackedOnAccountExtraComp = "rmv3_dn",
  InternationalTrackedParcels30kg = "rmv3_do",
  InternationalTrackedParcels30kgCPrio = "rmv3_dp",
  InternationalTrackedParcels30kgETOEC = "rmv3_dq",
  InternationalTrackedParcels30kgETOEE = "rmv3_dr",
  InternationalTrackedParcels30kgExtraComp = "rmv3_ds",
  InternationalTrackedParcels30kgExtraCompETOEC = "rmv3_dt",
  InternationalTrackedParcels30kgExtraCompETOEE = "rmv3_du",
  InternationalTrackedParcels30kgXCompCPrio = "rmv3_dv",
  InternationalTrackedSignedOnAccount = "rmv3_dw",
  InternationalTrackedSignedOnAccountExtraComp = "rmv3_dx",
  ParcelpostFlatRateAnnual = "rmv3_dy",
  RM24LargeLetterAnnualFlatRate = "rmv3_dz",
  RM24PresortedLargeLetterAnnualFlatRate = "rmv3_ea",
  RM24PresortedParcelsAnnualFlatRate = "rmv3_eb",
  RM48LargeLetterAnnualFlatRate = "rmv3_ec",
  RM48PresortedLargeLetterAnnualFlatRate = "rmv3_ed",
  RM48PresortedParcelsAnnualFlatRate = "rmv3_ee",
  RoyalMail1stClass = "rmv3_ef",
  RoyalMail1stClassLettersDailyRateservice = "rmv3_eg",
  RoyalMail1stClassSignedFor = "rmv3_eh",
  RoyalMail24LargeLetterDailyRate = "rmv3_ei",
  RoyalMail24LargeLetterFlatRate = "rmv3_ej",
  RoyalMail24LargeLetterFlatRateService = "rmv3_ek",
  RoyalMail24PacketpostFlatRateService = "rmv3_el",
  RoyalMail24ParcelDailyRateService = "rmv3_em",
  RoyalMail24ParcelSort8DailyRateService = "rmv3_en",
  RoyalMail24ParcelSort8FlatRateService = "rmv3_eo",
  RoyalMail24ParcelsDailyRate = "rmv3_ep",
  RoyalMail24ParcelsFlatRate = "rmv3_eq",
  RoyalMail24PresortedLargeLetter = "rmv3_er",
  RoyalMail24PresortedParcels = "rmv3_es",
  RoyalMail24SORT8LargeLetterAnnualFlatRate = "rmv3_et",
  RoyalMail24SORT8ParcelsAnnualFlatRate = "rmv3_eu",
  RoyalMail2ndClass = "rmv3_ev",
  RoyalMail2ndClassLettersDailyRateservice = "rmv3_ew",
  RoyalMail2ndClassSignedFor = "rmv3_ex",
  RoyalMail48LargeLetterDailyRate = "rmv3_ey",
  RoyalMail48LargeLetterFlatRate = "rmv3_ez",
  RoyalMail48LargeLetterFlatRateService = "rmv3_fa",
  RoyalMail48PacketpostFlatRateService = "rmv3_fb",
  RoyalMail48ParcelDailyRateService = "rmv3_fc",
  RoyalMail48ParcelSort8DailyRateService = "rmv3_fd",
  RoyalMail48ParcelSort8FlatRateService = "rmv3_fe",
  RoyalMail48ParcelsDailyRate = "rmv3_ff",
  RoyalMail48ParcelsFlatRate = "rmv3_fg",
  RoyalMail48PresortedLargeLetter = "rmv3_fh",
  RoyalMail48PresortedParcels = "rmv3_fi",
  RoyalMail48SORT8LargeLetterAnnualFlatRate = "rmv3_fj",
  RoyalMail48Sort8ParcelsAnnualFlatRate = "rmv3_fk",
  SpecialDeliveryGuaranteed1000 = "rmv3_fl",
  SpecialDeliveryGuaranteed2500 = "rmv3_fm",
  SpecialDeliveryGuaranteed750 = "rmv3_fn",
  SpecialDeliveryGuaranteedAGE1000 = "rmv3_fo",
  SpecialDeliveryGuaranteedAGE2500 = "rmv3_fp",
  SpecialDeliveryGuaranteedAGE750 = "rmv3_fq",
  SpecialDeliveryGuaranteedBy1PM1000 = "rmv3_fr",
  SpecialDeliveryGuaranteedBy1PM2500 = "rmv3_fs",
  SpecialDeliveryGuaranteedBy1PM750 = "rmv3_ft",
  SpecialDeliveryGuaranteedBy1PMAGE1000 = "rmv3_fu",
  SpecialDeliveryGuaranteedBy1PMAGE2500 = "rmv3_fv",
  SpecialDeliveryGuaranteedBy1PMAGE750 = "rmv3_fw",
  SpecialDeliveryGuaranteedBy1PMID1000 = "rmv3_fx",
  SpecialDeliveryGuaranteedBy1PMID2500 = "rmv3_fy",
  SpecialDeliveryGuaranteedBy1PMID750 = "rmv3_fz",
  SpecialDeliveryGuaranteedBy9AM1000 = "rmv3_ga",
  SpecialDeliveryGuaranteedBy9AM2500 = "rmv3_gb",
  SpecialDeliveryGuaranteedBy9AM750 = "rmv3_gc",
  SpecialDeliveryGuaranteedBy9AMAGE1000 = "rmv3_gd",
  SpecialDeliveryGuaranteedBy9AMAGE2500 = "rmv3_ge",
  SpecialDeliveryGuaranteedBy9AMAGE750 = "rmv3_gf",
  SpecialDeliveryGuaranteedBy9AMID1000 = "rmv3_gg",
  SpecialDeliveryGuaranteedBy9AMID2500 = "rmv3_gh",
  SpecialDeliveryGuaranteedBy9AMID750 = "rmv3_gi",
  SpecialDeliveryGuaranteedID1000 = "rmv3_gj",
  SpecialDeliveryGuaranteedID2500 = "rmv3_gk",
  SpecialDeliveryGuaranteedID750 = "rmv3_gl",
  Tracked24 = "rmv3_gm",
  Tracked24HighVolume = "rmv3_gn",
  Tracked24HighVolumeSignatureAGE = "rmv3_go",
  Tracked24SignatureAGE = "rmv3_gp",
  Tracked48 = "rmv3_gq",
  Tracked48HighVolume = "rmv3_gr",
  Tracked48HighVolumeSignatureAGE = "rmv3_gs",
  Tracked48SignatureAGE = "rmv3_gt",
  TrackedLetterBoxable24 = "rmv3_gu",
  TrackedLetterBoxable24HighVolume = "rmv3_gv",
  TrackedLetterBoxable48 = "rmv3_gw",
  TrackedLetterBoxable48HighVolume = "rmv3_gx",
  TrackedReturns24 = "rmv3_gy",
  TrackedReturns48 = "rmv3_gz",
  EuropriorityDDP = "rmv3_ha",
  EuropriorityDDPComp1 = "rmv3_hb",
  EuropriorityDDPComp2 = "rmv3_hc",
  EuropriorityDDPComp3 = "rmv3_hd",
  EuropriorityDTPIOSS = "rmv3_he",
  EuropriorityDTPIOSSComp1 = "rmv3_hf",
  EuropriorityDTPIOSSComp2 = "rmv3_hg",
  EuropriorityDTPIOSSComp3 = "rmv3_hh",
  Express10 = "rmv3_hi",
  Express10Comp1 = "rmv3_hj",
  Express10Comp2 = "rmv3_hk",
  Express10Comp3 = "rmv3_hl",
  Express24 = "rmv3_hm",
  Express24Comp1 = "rmv3_hn",
  Express24Comp2 = "rmv3_ho",
  Express24Comp3 = "rmv3_hp",
  Express24Weekend = "rmv3_hq",
  Express24WeekendComp1 = "rmv3_hr",
  Express24WeekendComp2 = "rmv3_hs",
  Express24WeekendComp3 = "rmv3_ht",
  Express48 = "rmv3_hu",
  Express48Comp1 = "rmv3_hv",
  Express48Comp2 = "rmv3_hw",
  Express48Comp3 = "rmv3_hx",
  Express48Large = "rmv3_hy",
  Express48LargeComp1 = "rmv3_hz",
  Express48LargeComp2 = "rmv3_ia",
  Express48LargeComp3 = "rmv3_ib",
  ExpressAM = "rmv3_ic",
  ExpressAMComp1 = "rmv3_id",
  ExpressAMComp2 = "rmv3_ie",
  ExpressAMComp3 = "rmv3_if",
  GlobalpriorityEurope = "rmv3_ig",
  GlobalpriorityEuropeComp1 = "rmv3_ih",
  GlobalpriorityEuropeComp2 = "rmv3_ii",
  GlobalpriorityEuropeComp3 = "rmv3_ij",
  GlobalpriorityROW = "rmv3_ik",
  GlobalpriorityROWComp1 = "rmv3_il",
  GlobalpriorityROWComp2 = "rmv3_im",
  GlobalpriorityROWComp3 = "rmv3_in",
  InternationalBusinessParcelsTrackedPriority = "rmv3_io",
  Tracked24HighVolumePIN = "rmv3_ip",
  Tracked24PIN = "rmv3_iq",
  Tracked48HighVolumePIN = "rmv3_ir",
  Tracked48PIN = "rmv3_is",
  TrackedImport24HighVolumePIN = "rmv3_it",
  TrackedImport48HighVolumePIN = "rmv3_iu",
  InternationalBusinessParcelsZeroSortLowVolPriority = "rmv3_iv",
}

const royalMailV3ServiceToServiceCodeConstant: Record<
  RoyalMailV3Services,
  RoyalMailV3ServiceCodeConstant
> = {
  [RoyalMailV3Services.RoyalMailV3]: RoyalMailV3ServiceCodeConstant.RoyalMailV3,
  [RoyalMailV3Services.EuropriorityDDP]:
    RoyalMailV3ServiceCodeConstant.EuropriorityDDP,
  [RoyalMailV3Services.EuropriorityDDPComp1]:
    RoyalMailV3ServiceCodeConstant.EuropriorityDDPComp1,
  [RoyalMailV3Services.EuropriorityDDPComp2]:
    RoyalMailV3ServiceCodeConstant.EuropriorityDDPComp2,
  [RoyalMailV3Services.EuropriorityDDPComp3]:
    RoyalMailV3ServiceCodeConstant.EuropriorityDDPComp3,
  [RoyalMailV3Services.EuropriorityDTPIOSS]:
    RoyalMailV3ServiceCodeConstant.EuropriorityDTPIOSS,
  [RoyalMailV3Services.EuropriorityDTPIOSSComp1]:
    RoyalMailV3ServiceCodeConstant.EuropriorityDTPIOSSComp1,
  [RoyalMailV3Services.EuropriorityDTPIOSSComp2]:
    RoyalMailV3ServiceCodeConstant.EuropriorityDTPIOSSComp2,
  [RoyalMailV3Services.EuropriorityDTPIOSSComp3]:
    RoyalMailV3ServiceCodeConstant.EuropriorityDTPIOSSComp3,
  [RoyalMailV3Services.Express10]: RoyalMailV3ServiceCodeConstant.Express10,
  [RoyalMailV3Services.Express10Comp1]:
    RoyalMailV3ServiceCodeConstant.Express10Comp1,
  [RoyalMailV3Services.Express10Comp2]:
    RoyalMailV3ServiceCodeConstant.Express10Comp2,
  [RoyalMailV3Services.Express10Comp3]:
    RoyalMailV3ServiceCodeConstant.Express10Comp3,
  [RoyalMailV3Services.Express24]: RoyalMailV3ServiceCodeConstant.Express24,
  [RoyalMailV3Services.Express24Comp1]:
    RoyalMailV3ServiceCodeConstant.Express24Comp1,
  [RoyalMailV3Services.Express24Comp2]:
    RoyalMailV3ServiceCodeConstant.Express24Comp2,
  [RoyalMailV3Services.Express24Comp3]:
    RoyalMailV3ServiceCodeConstant.Express24Comp3,
  [RoyalMailV3Services.Express24Weekend]:
    RoyalMailV3ServiceCodeConstant.Express24Weekend,
  [RoyalMailV3Services.Express24WeekendComp1]:
    RoyalMailV3ServiceCodeConstant.Express24WeekendComp1,
  [RoyalMailV3Services.Express24WeekendComp2]:
    RoyalMailV3ServiceCodeConstant.Express24WeekendComp2,
  [RoyalMailV3Services.Express24WeekendComp3]:
    RoyalMailV3ServiceCodeConstant.Express24WeekendComp3,
  [RoyalMailV3Services.Express48]: RoyalMailV3ServiceCodeConstant.Express48,
  [RoyalMailV3Services.Express48Comp1]:
    RoyalMailV3ServiceCodeConstant.Express48Comp1,
  [RoyalMailV3Services.Express48Comp2]:
    RoyalMailV3ServiceCodeConstant.Express48Comp2,
  [RoyalMailV3Services.Express48Comp3]:
    RoyalMailV3ServiceCodeConstant.Express48Comp3,
  [RoyalMailV3Services.Express48Large]:
    RoyalMailV3ServiceCodeConstant.Express48Large,
  [RoyalMailV3Services.Express48LargeComp1]:
    RoyalMailV3ServiceCodeConstant.Express48LargeComp1,
  [RoyalMailV3Services.Express48LargeComp2]:
    RoyalMailV3ServiceCodeConstant.Express48LargeComp2,
  [RoyalMailV3Services.Express48LargeComp3]:
    RoyalMailV3ServiceCodeConstant.Express48LargeComp3,
  [RoyalMailV3Services.ExpressAM]: RoyalMailV3ServiceCodeConstant.ExpressAM,
  [RoyalMailV3Services.ExpressAMComp1]:
    RoyalMailV3ServiceCodeConstant.ExpressAMComp1,
  [RoyalMailV3Services.ExpressAMComp2]:
    RoyalMailV3ServiceCodeConstant.ExpressAMComp2,
  [RoyalMailV3Services.ExpressAMComp3]:
    RoyalMailV3ServiceCodeConstant.ExpressAMComp3,
  [RoyalMailV3Services.GlobalpriorityEurope]:
    RoyalMailV3ServiceCodeConstant.GlobalpriorityEurope,
  [RoyalMailV3Services.GlobalpriorityEuropeComp1]:
    RoyalMailV3ServiceCodeConstant.GlobalpriorityEuropeComp1,
  [RoyalMailV3Services.GlobalpriorityEuropeComp2]:
    RoyalMailV3ServiceCodeConstant.GlobalpriorityEuropeComp2,
  [RoyalMailV3Services.GlobalpriorityEuropeComp3]:
    RoyalMailV3ServiceCodeConstant.GlobalpriorityEuropeComp3,
  [RoyalMailV3Services.GlobalpriorityROW]:
    RoyalMailV3ServiceCodeConstant.GlobalpriorityROW,
  [RoyalMailV3Services.GlobalpriorityROWComp1]:
    RoyalMailV3ServiceCodeConstant.GlobalpriorityROWComp1,
  [RoyalMailV3Services.GlobalpriorityROWComp2]:
    RoyalMailV3ServiceCodeConstant.GlobalpriorityROWComp2,
  [RoyalMailV3Services.GlobalpriorityROWComp3]:
    RoyalMailV3ServiceCodeConstant.GlobalpriorityROWComp3,
  [RoyalMailV3Services.InternationalBusinessParcelsTrackedPriority]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessParcelsTrackedPriority,
  [RoyalMailV3Services.Tracked24HighVolumePIN]:
    RoyalMailV3ServiceCodeConstant.Tracked24HighVolumePIN,
  [RoyalMailV3Services.Tracked24PIN]:
    RoyalMailV3ServiceCodeConstant.Tracked24PIN,
  [RoyalMailV3Services.Tracked48HighVolumePIN]:
    RoyalMailV3ServiceCodeConstant.Tracked48HighVolumePIN,
  [RoyalMailV3Services.Tracked48PIN]:
    RoyalMailV3ServiceCodeConstant.Tracked48PIN,
  [RoyalMailV3Services.TrackedImport24HighVolumePIN]:
    RoyalMailV3ServiceCodeConstant.TrackedImport24HighVolumePIN,
  [RoyalMailV3Services.TrackedImport48HighVolumePIN]:
    RoyalMailV3ServiceCodeConstant.TrackedImport48HighVolumePIN,
  [RoyalMailV3Services.DEImportRoyalMail24PlusParcelsWeekendService]:
    RoyalMailV3ServiceCodeConstant.DEImportRoyalMail24PlusParcelsWeekendService,
  [RoyalMailV3Services.DEImportStandard24LargeLetter]:
    RoyalMailV3ServiceCodeConstant.DEImportStandard24LargeLetter,
  [RoyalMailV3Services.DEImportStandard24LargeLetterWeekendService]:
    RoyalMailV3ServiceCodeConstant.DEImportStandard24LargeLetterWeekendService,
  [RoyalMailV3Services.DEImportStandard24Parcel]:
    RoyalMailV3ServiceCodeConstant.DEImportStandard24Parcel,
  [RoyalMailV3Services.DEImportStandard24ParcelWeekendService]:
    RoyalMailV3ServiceCodeConstant.DEImportStandard24ParcelWeekendService,
  [RoyalMailV3Services.DEImportStandard48LargeLetter]:
    RoyalMailV3ServiceCodeConstant.DEImportStandard48LargeLetter,
  [RoyalMailV3Services.DEImportStandard48LargeLetterWeekendService]:
    RoyalMailV3ServiceCodeConstant.DEImportStandard48LargeLetterWeekendService,
  [RoyalMailV3Services.DEImportTracked24ParcelBoxableHighVolumeWeekendService]:
    RoyalMailV3ServiceCodeConstant.DEImportTracked24ParcelBoxableHighVolumeWeekendService,
  [RoyalMailV3Services.DEImportTracked24ParcelBoxableWeekendService]:
    RoyalMailV3ServiceCodeConstant.DEImportTracked24ParcelBoxableWeekendService,
  [RoyalMailV3Services.DEImportTracked24ParcelHighVolumeWeekendService]:
    RoyalMailV3ServiceCodeConstant.DEImportTracked24ParcelHighVolumeWeekendService,
  [RoyalMailV3Services.DEImportTracked24SignatureParcelHighVolumeWeekendServiceAGE]:
    RoyalMailV3ServiceCodeConstant.DEImportTracked24SignatureParcelHighVolumeWeekendServiceAGE,
  [RoyalMailV3Services.DEImportTracked48ParcelBoxableHighVolumeWeekendService]:
    RoyalMailV3ServiceCodeConstant.DEImportTracked48ParcelBoxableHighVolumeWeekendService,
  [RoyalMailV3Services.DEImportTracked48ParcelBoxableWeekendService]:
    RoyalMailV3ServiceCodeConstant.DEImportTracked48ParcelBoxableWeekendService,
  [RoyalMailV3Services.DEImportTracked48ParcelHighVolumeWeekendService]:
    RoyalMailV3ServiceCodeConstant.DEImportTracked48ParcelHighVolumeWeekendService,
  [RoyalMailV3Services.DEImportTracked48SignatureParcelHighVolumeAGE]:
    RoyalMailV3ServiceCodeConstant.DEImportTracked48SignatureParcelHighVolumeAGE,
  [RoyalMailV3Services.DEImportTracked48SignatureParcelHighVolumeWeekendServiceAGE]:
    RoyalMailV3ServiceCodeConstant.DEImportTracked48SignatureParcelHighVolumeWeekendServiceAGE,
  [RoyalMailV3Services.DEImporttoEUMaxSortLargeLetter]:
    RoyalMailV3ServiceCodeConstant.DEImporttoEUMaxSortLargeLetter,
  [RoyalMailV3Services.DEImporttoEUMaxSortParcel]:
    RoyalMailV3ServiceCodeConstant.DEImporttoEUMaxSortParcel,
  [RoyalMailV3Services.DEImporttoEUSignedParcel]:
    RoyalMailV3ServiceCodeConstant.DEImporttoEUSignedParcel,
  [RoyalMailV3Services.DEImporttoEUTrackedHighVolLargeLetter]:
    RoyalMailV3ServiceCodeConstant.DEImporttoEUTrackedHighVolLargeLetter,
  [RoyalMailV3Services.DEImporttoEUTrackedParcel]:
    RoyalMailV3ServiceCodeConstant.DEImporttoEUTrackedParcel,
  [RoyalMailV3Services.DEImporttoEUTrackedSignedLargeLetter]:
    RoyalMailV3ServiceCodeConstant.DEImporttoEUTrackedSignedLargeLetter,
  [RoyalMailV3Services.DEImporttoEUTrackedSignedParcel]:
    RoyalMailV3ServiceCodeConstant.DEImporttoEUTrackedSignedParcel,
  [RoyalMailV3Services.HMForcesMail]:
    RoyalMailV3ServiceCodeConstant.HMForcesMail,
  [RoyalMailV3Services.HMForcesSignedFor]:
    RoyalMailV3ServiceCodeConstant.HMForcesSignedFor,
  [RoyalMailV3Services.HMForcesSpecialDelivery1000]:
    RoyalMailV3ServiceCodeConstant.HMForcesSpecialDelivery1000,
  [RoyalMailV3Services.HMForcesSpecialDelivery2500]:
    RoyalMailV3ServiceCodeConstant.HMForcesSpecialDelivery2500,
  [RoyalMailV3Services.HMForcesSpecialDelivery500]:
    RoyalMailV3ServiceCodeConstant.HMForcesSpecialDelivery500,
  [RoyalMailV3Services.ImportDETracked24]:
    RoyalMailV3ServiceCodeConstant.ImportDETracked24,
  [RoyalMailV3Services.ImportDETracked24HighVolume]:
    RoyalMailV3ServiceCodeConstant.ImportDETracked24HighVolume,
  [RoyalMailV3Services.ImportDETracked24Letterboxable]:
    RoyalMailV3ServiceCodeConstant.ImportDETracked24Letterboxable,
  [RoyalMailV3Services.ImportDETracked24LetterboxableHighVolume]:
    RoyalMailV3ServiceCodeConstant.ImportDETracked24LetterboxableHighVolume,
  [RoyalMailV3Services.ImportDETracked24SignatureHighVolumeAGE]:
    RoyalMailV3ServiceCodeConstant.ImportDETracked24SignatureHighVolumeAGE,
  [RoyalMailV3Services.ImportDETracked24SignatureStandardAGE]:
    RoyalMailV3ServiceCodeConstant.ImportDETracked24SignatureStandardAGE,
  [RoyalMailV3Services.ImportDETracked48]:
    RoyalMailV3ServiceCodeConstant.ImportDETracked48,
  [RoyalMailV3Services.ImportDETracked48HighVolume]:
    RoyalMailV3ServiceCodeConstant.ImportDETracked48HighVolume,
  [RoyalMailV3Services.ImportDETracked48Letterboxable]:
    RoyalMailV3ServiceCodeConstant.ImportDETracked48Letterboxable,
  [RoyalMailV3Services.ImportDETracked48LetterboxableHighVolume]:
    RoyalMailV3ServiceCodeConstant.ImportDETracked48LetterboxableHighVolume,
  [RoyalMailV3Services.ImportDETrackedReturns24]:
    RoyalMailV3ServiceCodeConstant.ImportDETrackedReturns24,
  [RoyalMailV3Services.ImportDETrackedReturns48]:
    RoyalMailV3ServiceCodeConstant.ImportDETrackedReturns48,
  [RoyalMailV3Services.InternationalBusinessMailLargeLetterCountryPricedPriority]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessMailLargeLetterCountryPricedPriority,
  [RoyalMailV3Services.InternationalBusinessMailLargeLetterCountryPricedPriorityETOE]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessMailLargeLetterCountryPricedPriorityETOE,
  [RoyalMailV3Services.InternationalBusinessMailLargeLetterCountrySortPriority]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessMailLargeLetterCountrySortPriority,
  [RoyalMailV3Services.InternationalBusinessMailLargeLetterMaxSortPriorityService]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessMailLargeLetterMaxSortPriorityService,
  [RoyalMailV3Services.InternationalBusinessMailLargeLetterMaxSortResidueStandard]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessMailLargeLetterMaxSortResidueStandard,
  [RoyalMailV3Services.InternationalBusinessMailLargeLetterMaxSortStandardService]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessMailLargeLetterMaxSortStandardService,
  [RoyalMailV3Services.InternationalBusinessMailLargeLetterSemiTracked]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessMailLargeLetterSemiTracked,
  [RoyalMailV3Services.InternationalBusinessMailLargeLetterZeroSortPriority]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessMailLargeLetterZeroSortPriority,
  [RoyalMailV3Services.InternationalBusinessMailLargeLetterZeroSortPriorityMachine]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessMailLargeLetterZeroSortPriorityMachine,
  [RoyalMailV3Services.InternationalBusinessMailLargeLetterZoneSortPriority]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessMailLargeLetterZoneSortPriority,
  [RoyalMailV3Services.InternationalBusinessMailLargeLetterZoneSortPriorityMachine]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessMailLargeLetterZoneSortPriorityMachine,
  [RoyalMailV3Services.InternationalBusinessMailLetterMaxSortResidueStandard]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessMailLetterMaxSortResidueStandard,
  [RoyalMailV3Services.InternationalBusinessMailLettersMaxSortStandard]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessMailLettersMaxSortStandard,
  [RoyalMailV3Services.InternationalBusinessMailLettersZeroSortPriority]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessMailLettersZeroSortPriority,
  [RoyalMailV3Services.InternationalBusinessMailLettersZoneSortPriority]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessMailLettersZoneSortPriority,
  [RoyalMailV3Services.InternationalBusinessMailSignedCountryPriced]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessMailSignedCountryPriced,
  [RoyalMailV3Services.InternationalBusinessMailSignedExtraCompensationCountryPriced]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessMailSignedExtraCompensationCountryPriced,
  [RoyalMailV3Services.InternationalBusinessMailSignedHighVolCountryPriced]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessMailSignedHighVolCountryPriced,
  [RoyalMailV3Services.InternationalBusinessMailSignedZoneSort]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessMailSignedZoneSort,
  [RoyalMailV3Services.InternationalBusinessMailTrackedCountryPriced]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessMailTrackedCountryPriced,
  [RoyalMailV3Services.InternationalBusinessMailTrackedHighVolCountryPriced]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessMailTrackedHighVolCountryPriced,
  [RoyalMailV3Services.InternationalBusinessMailTrackedHighVolExtraCompCountryPriced]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessMailTrackedHighVolExtraCompCountryPriced,
  [RoyalMailV3Services.InternationalBusinessMailTrackedLargeLetterCountryPricedETOE]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessMailTrackedLargeLetterCountryPricedETOE,
  [RoyalMailV3Services.InternationalBusinessMailTrackedSignedCountryPriced]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessMailTrackedSignedCountryPriced,
  [RoyalMailV3Services.InternationalBusinessMailTrackedSignedHighVolCountryPriced]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessMailTrackedSignedHighVolCountryPriced,
  [RoyalMailV3Services.InternationalBusinessMailTrackedSignedHighVolExtraCompCountryPriced]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessMailTrackedSignedHighVolExtraCompCountryPriced,
  [RoyalMailV3Services.InternationalBusinessMailTrackedSignedLargeLetterCountryPricedETOE]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessMailTrackedSignedLargeLetterCountryPricedETOE,
  [RoyalMailV3Services.InternationalBusinessMailTrackedSignedZoneSort]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessMailTrackedSignedZoneSort,
  [RoyalMailV3Services.InternationalBusinessMailTrackedZoneSort]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessMailTrackedZoneSort,
  [RoyalMailV3Services.InternationalBusinessPCTrackedSignedLargeLetterHVExtraCompCountryPricedETOE]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessPCTrackedSignedLargeLetterHVExtraCompCountryPricedETOE,
  [RoyalMailV3Services.InternationalBusinessParcelPriorityCountryPricedBoxable]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessParcelPriorityCountryPricedBoxable,
  [RoyalMailV3Services.InternationalBusinessParcelPriorityCountryPricedBoxableDDP]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessParcelPriorityCountryPricedBoxableDDP,
  [RoyalMailV3Services.InternationalBusinessParcelTrackedCountryPriced]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessParcelTrackedCountryPriced,
  [RoyalMailV3Services.InternationalBusinessParcelTrackedCountryPricedBoxable]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessParcelTrackedCountryPricedBoxable,
  [RoyalMailV3Services.InternationalBusinessParcelTrackedCountryPricedBoxableDDP]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessParcelTrackedCountryPricedBoxableDDP,
  [RoyalMailV3Services.InternationalBusinessParcelTrackedCountryPricedBoxableExtraComp]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessParcelTrackedCountryPricedBoxableExtraComp,
  [RoyalMailV3Services.InternationalBusinessParcels]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessParcels,
  [RoyalMailV3Services.InternationalBusinessParcelsPrintDirectPriority]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessParcelsPrintDirectPriority,
  [RoyalMailV3Services.InternationalBusinessParcelsPrintDirectStandard]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessParcelsPrintDirectStandard,
  [RoyalMailV3Services.InternationalBusinessParcelsSignedCountryPriced]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessParcelsSignedCountryPriced,
  [RoyalMailV3Services.InternationalBusinessParcelsSignedExtraCompensationCountryPriced]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessParcelsSignedExtraCompensationCountryPriced,
  [RoyalMailV3Services.InternationalBusinessParcelsSignedExtraCompensationZoneSort]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessParcelsSignedExtraCompensationZoneSort,
  [RoyalMailV3Services.InternationalBusinessParcelsSignedZoneSort]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessParcelsSignedZoneSort,
  [RoyalMailV3Services.InternationalBusinessParcelsTrackedCountryPriced]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessParcelsTrackedCountryPriced,
  [RoyalMailV3Services.InternationalBusinessParcelsTrackedCountryPricedETOE]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessParcelsTrackedCountryPricedETOE,
  [RoyalMailV3Services.InternationalBusinessParcelsTrackedDirectIrelandCountry]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessParcelsTrackedDirectIrelandCountry,
  [RoyalMailV3Services.InternationalBusinessParcelsTrackedExtraCompCountryPriced]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessParcelsTrackedExtraCompCountryPriced,
  [RoyalMailV3Services.InternationalBusinessParcelsTrackedExtraCompZoneSort]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessParcelsTrackedExtraCompZoneSort,
  [RoyalMailV3Services.InternationalBusinessParcelsTrackedSignedCountryPriced]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessParcelsTrackedSignedCountryPriced,
  [RoyalMailV3Services.InternationalBusinessParcelsTrackedSignedCountryPricedETOE]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessParcelsTrackedSignedCountryPricedETOE,
  [RoyalMailV3Services.InternationalBusinessParcelsTrackedSignedDDP]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessParcelsTrackedSignedDDP,
  [RoyalMailV3Services.InternationalBusinessParcelsTrackedSignedExtraCompensationCountryPriced]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessParcelsTrackedSignedExtraCompensationCountryPriced,
  [RoyalMailV3Services.InternationalBusinessParcelsTrackedSignedExtraCompensationZoneSort]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessParcelsTrackedSignedExtraCompensationZoneSort,
  [RoyalMailV3Services.InternationalBusinessParcelsTrackedSignedZoneSort]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessParcelsTrackedSignedZoneSort,
  [RoyalMailV3Services.InternationalBusinessParcelsTrackedZoneSort]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessParcelsTrackedZoneSort,
  [RoyalMailV3Services.InternationalBusinessParcelsZeroSortLowVolPriority]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessParcelsZeroSortLowVolPriority,
  [RoyalMailV3Services.InternationalBusinessParcelsZeroSortPriority]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessParcelsZeroSortPriority,
  [RoyalMailV3Services.InternationalBusinessParcelsZeroSortPriorityETOE]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessParcelsZeroSortPriorityETOE,
  [RoyalMailV3Services.InternationalBusinessParcelsZoneSortPriorityService]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessParcelsZoneSortPriorityService,
  [RoyalMailV3Services.InternationalBusinessPersonalCorrespondenceLargeLetterCountrySortPriorityETOE]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessPersonalCorrespondenceLargeLetterCountrySortPriorityETOE,
  [RoyalMailV3Services.InternationalBusinessPersonalCorrespondenceLetterPriorityUntracked]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessPersonalCorrespondenceLetterPriorityUntracked,
  [RoyalMailV3Services.InternationalBusinessPersonalCorrespondenceLetterPriorityUntrackedETOE]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessPersonalCorrespondenceLetterPriorityUntrackedETOE,
  [RoyalMailV3Services.InternationalBusinessPersonalCorrespondenceLetterTrackedHighVolCountryPricedETOE]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessPersonalCorrespondenceLetterTrackedHighVolCountryPricedETOE,
  [RoyalMailV3Services.InternationalBusinessPersonalCorrespondenceLetterTrackedSignedHighVolCountryPricedETOE]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessPersonalCorrespondenceLetterTrackedSignedHighVolCountryPricedETOE,
  [RoyalMailV3Services.InternationalBusinessPersonalCorrespondenceMaxSortLetter]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessPersonalCorrespondenceMaxSortLetter,
  [RoyalMailV3Services.InternationalBusinessPersonalCorrespondenceMaxSortResidueLargeLetter]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessPersonalCorrespondenceMaxSortResidueLargeLetter,
  [RoyalMailV3Services.InternationalBusinessPersonalCorrespondenceMaxSortResidueLetter]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessPersonalCorrespondenceMaxSortResidueLetter,
  [RoyalMailV3Services.InternationalBusinessPersonalCorrespondenceSignedLargeLetterExtraCompensationCountryPricedETOE]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessPersonalCorrespondenceSignedLargeLetterExtraCompensationCountryPricedETOE,
  [RoyalMailV3Services.InternationalBusinessPersonalCorrespondenceSignedLetterHighVolCountryPricedETOE]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessPersonalCorrespondenceSignedLetterHighVolCountryPricedETOE,
  [RoyalMailV3Services.InternationalBusinessPersonalCorrespondenceTrackedLargeLetterHighVolExtraCompCountryPricedETOE]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessPersonalCorrespondenceTrackedLargeLetterHighVolExtraCompCountryPricedETOE,
  [RoyalMailV3Services.InternationalBusinessPrintedMatterPacket]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessPrintedMatterPacket,
  [RoyalMailV3Services.InternationalBusinessTrackedExpressNPC]:
    RoyalMailV3ServiceCodeConstant.InternationalBusinessTrackedExpressNPC,
  [RoyalMailV3Services.InternationalEconomyOnAccount]:
    RoyalMailV3ServiceCodeConstant.InternationalEconomyOnAccount,
  [RoyalMailV3Services.InternationalSignedOnAccount]:
    RoyalMailV3ServiceCodeConstant.InternationalSignedOnAccount,
  [RoyalMailV3Services.InternationalSignedOnAccountExtraComp]:
    RoyalMailV3ServiceCodeConstant.InternationalSignedOnAccountExtraComp,
  [RoyalMailV3Services.InternationalStandardOnAccount]:
    RoyalMailV3ServiceCodeConstant.InternationalStandardOnAccount,
  [RoyalMailV3Services.InternationalTrackedOnAccount]:
    RoyalMailV3ServiceCodeConstant.InternationalTrackedOnAccount,
  [RoyalMailV3Services.InternationalTrackedOnAccountExtraComp]:
    RoyalMailV3ServiceCodeConstant.InternationalTrackedOnAccountExtraComp,
  [RoyalMailV3Services.InternationalTrackedParcels30kg]:
    RoyalMailV3ServiceCodeConstant.InternationalTrackedParcels30kg,
  [RoyalMailV3Services.InternationalTrackedParcels30kgCPrio]:
    RoyalMailV3ServiceCodeConstant.InternationalTrackedParcels30kgCPrio,
  [RoyalMailV3Services.InternationalTrackedParcels30kgETOEC]:
    RoyalMailV3ServiceCodeConstant.InternationalTrackedParcels30kgETOEC,
  [RoyalMailV3Services.InternationalTrackedParcels30kgETOEE]:
    RoyalMailV3ServiceCodeConstant.InternationalTrackedParcels30kgETOEE,
  [RoyalMailV3Services.InternationalTrackedParcels30kgExtraComp]:
    RoyalMailV3ServiceCodeConstant.InternationalTrackedParcels30kgExtraComp,
  [RoyalMailV3Services.InternationalTrackedParcels30kgExtraCompETOEC]:
    RoyalMailV3ServiceCodeConstant.InternationalTrackedParcels30kgExtraCompETOEC,
  [RoyalMailV3Services.InternationalTrackedParcels30kgExtraCompETOEE]:
    RoyalMailV3ServiceCodeConstant.InternationalTrackedParcels30kgExtraCompETOEE,
  [RoyalMailV3Services.InternationalTrackedParcels30kgXCompCPrio]:
    RoyalMailV3ServiceCodeConstant.InternationalTrackedParcels30kgXCompCPrio,
  [RoyalMailV3Services.InternationalTrackedSignedOnAccount]:
    RoyalMailV3ServiceCodeConstant.InternationalTrackedSignedOnAccount,
  [RoyalMailV3Services.InternationalTrackedSignedOnAccountExtraComp]:
    RoyalMailV3ServiceCodeConstant.InternationalTrackedSignedOnAccountExtraComp,
  [RoyalMailV3Services.ParcelpostFlatRateAnnual]:
    RoyalMailV3ServiceCodeConstant.ParcelpostFlatRateAnnual,
  [RoyalMailV3Services.RM24LargeLetterAnnualFlatRate]:
    RoyalMailV3ServiceCodeConstant.RM24LargeLetterAnnualFlatRate,
  [RoyalMailV3Services.RM24PresortedLargeLetterAnnualFlatRate]:
    RoyalMailV3ServiceCodeConstant.RM24PresortedLargeLetterAnnualFlatRate,
  [RoyalMailV3Services.RM24PresortedParcelsAnnualFlatRate]:
    RoyalMailV3ServiceCodeConstant.RM24PresortedParcelsAnnualFlatRate,
  [RoyalMailV3Services.RM48LargeLetterAnnualFlatRate]:
    RoyalMailV3ServiceCodeConstant.RM48LargeLetterAnnualFlatRate,
  [RoyalMailV3Services.RM48PresortedLargeLetterAnnualFlatRate]:
    RoyalMailV3ServiceCodeConstant.RM48PresortedLargeLetterAnnualFlatRate,
  [RoyalMailV3Services.RM48PresortedParcelsAnnualFlatRate]:
    RoyalMailV3ServiceCodeConstant.RM48PresortedParcelsAnnualFlatRate,
  [RoyalMailV3Services.RoyalMail1stClass]:
    RoyalMailV3ServiceCodeConstant.RoyalMail1stClass,
  [RoyalMailV3Services.RoyalMail1stClassLettersDailyRateservice]:
    RoyalMailV3ServiceCodeConstant.RoyalMail1stClassLettersDailyRateservice,
  [RoyalMailV3Services.RoyalMail1stClassSignedFor]:
    RoyalMailV3ServiceCodeConstant.RoyalMail1stClassSignedFor,
  [RoyalMailV3Services.RoyalMail24LargeLetterDailyRate]:
    RoyalMailV3ServiceCodeConstant.RoyalMail24LargeLetterDailyRate,
  [RoyalMailV3Services.RoyalMail24LargeLetterFlatRate]:
    RoyalMailV3ServiceCodeConstant.RoyalMail24LargeLetterFlatRate,
  [RoyalMailV3Services.RoyalMail24LargeLetterFlatRateService]:
    RoyalMailV3ServiceCodeConstant.RoyalMail24LargeLetterFlatRateService,
  [RoyalMailV3Services.RoyalMail24PacketpostFlatRateService]:
    RoyalMailV3ServiceCodeConstant.RoyalMail24PacketpostFlatRateService,
  [RoyalMailV3Services.RoyalMail24ParcelDailyRateService]:
    RoyalMailV3ServiceCodeConstant.RoyalMail24ParcelDailyRateService,
  [RoyalMailV3Services.RoyalMail24ParcelSort8DailyRateService]:
    RoyalMailV3ServiceCodeConstant.RoyalMail24ParcelSort8DailyRateService,
  [RoyalMailV3Services.RoyalMail24ParcelSort8FlatRateService]:
    RoyalMailV3ServiceCodeConstant.RoyalMail24ParcelSort8FlatRateService,
  [RoyalMailV3Services.RoyalMail24ParcelsDailyRate]:
    RoyalMailV3ServiceCodeConstant.RoyalMail24ParcelsDailyRate,
  [RoyalMailV3Services.RoyalMail24ParcelsFlatRate]:
    RoyalMailV3ServiceCodeConstant.RoyalMail24ParcelsFlatRate,
  [RoyalMailV3Services.RoyalMail24PresortedLargeLetter]:
    RoyalMailV3ServiceCodeConstant.RoyalMail24PresortedLargeLetter,
  [RoyalMailV3Services.RoyalMail24PresortedParcels]:
    RoyalMailV3ServiceCodeConstant.RoyalMail24PresortedParcels,
  [RoyalMailV3Services.RoyalMail24SORT8LargeLetterAnnualFlatRate]:
    RoyalMailV3ServiceCodeConstant.RoyalMail24SORT8LargeLetterAnnualFlatRate,
  [RoyalMailV3Services.RoyalMail24SORT8ParcelsAnnualFlatRate]:
    RoyalMailV3ServiceCodeConstant.RoyalMail24SORT8ParcelsAnnualFlatRate,
  [RoyalMailV3Services.RoyalMail2ndClass]:
    RoyalMailV3ServiceCodeConstant.RoyalMail2ndClass,
  [RoyalMailV3Services.RoyalMail2ndClassLettersDailyRateservice]:
    RoyalMailV3ServiceCodeConstant.RoyalMail2ndClassLettersDailyRateservice,
  [RoyalMailV3Services.RoyalMail2ndClassSignedFor]:
    RoyalMailV3ServiceCodeConstant.RoyalMail2ndClassSignedFor,
  [RoyalMailV3Services.RoyalMail48LargeLetterDailyRate]:
    RoyalMailV3ServiceCodeConstant.RoyalMail48LargeLetterDailyRate,
  [RoyalMailV3Services.RoyalMail48LargeLetterFlatRate]:
    RoyalMailV3ServiceCodeConstant.RoyalMail48LargeLetterFlatRate,
  [RoyalMailV3Services.RoyalMail48LargeLetterFlatRateService]:
    RoyalMailV3ServiceCodeConstant.RoyalMail48LargeLetterFlatRateService,
  [RoyalMailV3Services.RoyalMail48PacketpostFlatRateService]:
    RoyalMailV3ServiceCodeConstant.RoyalMail48PacketpostFlatRateService,
  [RoyalMailV3Services.RoyalMail48ParcelDailyRateService]:
    RoyalMailV3ServiceCodeConstant.RoyalMail48ParcelDailyRateService,
  [RoyalMailV3Services.RoyalMail48ParcelSort8DailyRateService]:
    RoyalMailV3ServiceCodeConstant.RoyalMail48ParcelSort8DailyRateService,
  [RoyalMailV3Services.RoyalMail48ParcelSort8FlatRateService]:
    RoyalMailV3ServiceCodeConstant.RoyalMail48ParcelSort8FlatRateService,
  [RoyalMailV3Services.RoyalMail48ParcelsDailyRate]:
    RoyalMailV3ServiceCodeConstant.RoyalMail48ParcelsDailyRate,
  [RoyalMailV3Services.RoyalMail48ParcelsFlatRate]:
    RoyalMailV3ServiceCodeConstant.RoyalMail48ParcelsFlatRate,
  [RoyalMailV3Services.RoyalMail48PresortedLargeLetter]:
    RoyalMailV3ServiceCodeConstant.RoyalMail48PresortedLargeLetter,
  [RoyalMailV3Services.RoyalMail48PresortedParcels]:
    RoyalMailV3ServiceCodeConstant.RoyalMail48PresortedParcels,
  [RoyalMailV3Services.RoyalMail48SORT8LargeLetterAnnualFlatRate]:
    RoyalMailV3ServiceCodeConstant.RoyalMail48SORT8LargeLetterAnnualFlatRate,
  [RoyalMailV3Services.RoyalMail48Sort8ParcelsAnnualFlatRate]:
    RoyalMailV3ServiceCodeConstant.RoyalMail48Sort8ParcelsAnnualFlatRate,
  [RoyalMailV3Services.SpecialDeliveryGuaranteed1000]:
    RoyalMailV3ServiceCodeConstant.SpecialDeliveryGuaranteed1000,
  [RoyalMailV3Services.SpecialDeliveryGuaranteed2500]:
    RoyalMailV3ServiceCodeConstant.SpecialDeliveryGuaranteed2500,
  [RoyalMailV3Services.SpecialDeliveryGuaranteed750]:
    RoyalMailV3ServiceCodeConstant.SpecialDeliveryGuaranteed750,
  [RoyalMailV3Services.SpecialDeliveryGuaranteedAGE1000]:
    RoyalMailV3ServiceCodeConstant.SpecialDeliveryGuaranteedAGE1000,
  [RoyalMailV3Services.SpecialDeliveryGuaranteedAGE2500]:
    RoyalMailV3ServiceCodeConstant.SpecialDeliveryGuaranteedAGE2500,
  [RoyalMailV3Services.SpecialDeliveryGuaranteedAGE750]:
    RoyalMailV3ServiceCodeConstant.SpecialDeliveryGuaranteedAGE750,
  [RoyalMailV3Services.SpecialDeliveryGuaranteedBy1PM1000]:
    RoyalMailV3ServiceCodeConstant.SpecialDeliveryGuaranteedBy1PM1000,
  [RoyalMailV3Services.SpecialDeliveryGuaranteedBy1PM2500]:
    RoyalMailV3ServiceCodeConstant.SpecialDeliveryGuaranteedBy1PM2500,
  [RoyalMailV3Services.SpecialDeliveryGuaranteedBy1PM750]:
    RoyalMailV3ServiceCodeConstant.SpecialDeliveryGuaranteedBy1PM750,
  [RoyalMailV3Services.SpecialDeliveryGuaranteedBy1PMAGE1000]:
    RoyalMailV3ServiceCodeConstant.SpecialDeliveryGuaranteedBy1PMAGE1000,
  [RoyalMailV3Services.SpecialDeliveryGuaranteedBy1PMAGE2500]:
    RoyalMailV3ServiceCodeConstant.SpecialDeliveryGuaranteedBy1PMAGE2500,
  [RoyalMailV3Services.SpecialDeliveryGuaranteedBy1PMAGE750]:
    RoyalMailV3ServiceCodeConstant.SpecialDeliveryGuaranteedBy1PMAGE750,
  [RoyalMailV3Services.SpecialDeliveryGuaranteedBy1PMID1000]:
    RoyalMailV3ServiceCodeConstant.SpecialDeliveryGuaranteedBy1PMID1000,
  [RoyalMailV3Services.SpecialDeliveryGuaranteedBy1PMID2500]:
    RoyalMailV3ServiceCodeConstant.SpecialDeliveryGuaranteedBy1PMID2500,
  [RoyalMailV3Services.SpecialDeliveryGuaranteedBy1PMID750]:
    RoyalMailV3ServiceCodeConstant.SpecialDeliveryGuaranteedBy1PMID750,
  [RoyalMailV3Services.SpecialDeliveryGuaranteedBy9AM1000]:
    RoyalMailV3ServiceCodeConstant.SpecialDeliveryGuaranteedBy9AM1000,
  [RoyalMailV3Services.SpecialDeliveryGuaranteedBy9AM2500]:
    RoyalMailV3ServiceCodeConstant.SpecialDeliveryGuaranteedBy9AM2500,
  [RoyalMailV3Services.SpecialDeliveryGuaranteedBy9AM750]:
    RoyalMailV3ServiceCodeConstant.SpecialDeliveryGuaranteedBy9AM750,
  [RoyalMailV3Services.SpecialDeliveryGuaranteedBy9AMAGE1000]:
    RoyalMailV3ServiceCodeConstant.SpecialDeliveryGuaranteedBy9AMAGE1000,
  [RoyalMailV3Services.SpecialDeliveryGuaranteedBy9AMAGE2500]:
    RoyalMailV3ServiceCodeConstant.SpecialDeliveryGuaranteedBy9AMAGE2500,
  [RoyalMailV3Services.SpecialDeliveryGuaranteedBy9AMAGE750]:
    RoyalMailV3ServiceCodeConstant.SpecialDeliveryGuaranteedBy9AMAGE750,
  [RoyalMailV3Services.SpecialDeliveryGuaranteedBy9AMID1000]:
    RoyalMailV3ServiceCodeConstant.SpecialDeliveryGuaranteedBy9AMID1000,
  [RoyalMailV3Services.SpecialDeliveryGuaranteedBy9AMID2500]:
    RoyalMailV3ServiceCodeConstant.SpecialDeliveryGuaranteedBy9AMID2500,
  [RoyalMailV3Services.SpecialDeliveryGuaranteedBy9AMID750]:
    RoyalMailV3ServiceCodeConstant.SpecialDeliveryGuaranteedBy9AMID750,
  [RoyalMailV3Services.SpecialDeliveryGuaranteedID1000]:
    RoyalMailV3ServiceCodeConstant.SpecialDeliveryGuaranteedID1000,
  [RoyalMailV3Services.SpecialDeliveryGuaranteedID2500]:
    RoyalMailV3ServiceCodeConstant.SpecialDeliveryGuaranteedID2500,
  [RoyalMailV3Services.SpecialDeliveryGuaranteedID750]:
    RoyalMailV3ServiceCodeConstant.SpecialDeliveryGuaranteedID750,
  [RoyalMailV3Services.Tracked24]: RoyalMailV3ServiceCodeConstant.Tracked24,
  [RoyalMailV3Services.Tracked24HighVolume]:
    RoyalMailV3ServiceCodeConstant.Tracked24HighVolume,
  [RoyalMailV3Services.Tracked24HighVolumeSignatureAGE]:
    RoyalMailV3ServiceCodeConstant.Tracked24HighVolumeSignatureAGE,
  [RoyalMailV3Services.Tracked24SignatureAGE]:
    RoyalMailV3ServiceCodeConstant.Tracked24SignatureAGE,
  [RoyalMailV3Services.Tracked48]: RoyalMailV3ServiceCodeConstant.Tracked48,
  [RoyalMailV3Services.Tracked48HighVolume]:
    RoyalMailV3ServiceCodeConstant.Tracked48HighVolume,
  [RoyalMailV3Services.Tracked48HighVolumeSignatureAGE]:
    RoyalMailV3ServiceCodeConstant.Tracked48HighVolumeSignatureAGE,
  [RoyalMailV3Services.Tracked48SignatureAGE]:
    RoyalMailV3ServiceCodeConstant.Tracked48SignatureAGE,
  [RoyalMailV3Services.TrackedLetterBoxable24]:
    RoyalMailV3ServiceCodeConstant.TrackedLetterBoxable24,
  [RoyalMailV3Services.TrackedLetterBoxable24HighVolume]:
    RoyalMailV3ServiceCodeConstant.TrackedLetterBoxable24HighVolume,
  [RoyalMailV3Services.TrackedLetterBoxable48]:
    RoyalMailV3ServiceCodeConstant.TrackedLetterBoxable48,
  [RoyalMailV3Services.TrackedLetterBoxable48HighVolume]:
    RoyalMailV3ServiceCodeConstant.TrackedLetterBoxable48HighVolume,
  [RoyalMailV3Services.TrackedReturns24]:
    RoyalMailV3ServiceCodeConstant.TrackedReturns24,
  [RoyalMailV3Services.TrackedReturns48]:
    RoyalMailV3ServiceCodeConstant.TrackedReturns48,
};

enum SpeeDeeServiceCodeConstant {
  SpeeDeeDelivery = "sd_a",
}

const speeDeeServiceToServiceCodeConstant: Record<
  SpeeDeeServices,
  SpeeDeeServiceCodeConstant
> = {
  [SpeeDeeServices.SpeeDeeDelivery]: SpeeDeeServiceCodeConstant.SpeeDeeDelivery,
};

enum PassportGlobalServiceCodeConstant {
  PriorityDdpDelcon = "pg_a",
  PriorityDdp = "pg_b",
  epacketDdp = "pg_c",
  ExpressDdp = "pg_d",
  PriorityDduDelcon = "pg_e",
  PriorityDdu = "pg_f",
  ExpeditedDdpDelcon = "pg_g",
  ExpressDdpDelcon = "pg_h",
  GlobalPlus = "pg_i",
}

const passportGlobalServiceToServiceCodeConstant: Record<
  PassportGlobalServices,
  PassportGlobalServiceCodeConstant
> = {
  [PassportGlobalServices.PriorityDdpDelcon]:
    PassportGlobalServiceCodeConstant.PriorityDdpDelcon,
  [PassportGlobalServices.PriorityDdp]:
    PassportGlobalServiceCodeConstant.PriorityDdp,
  [PassportGlobalServices.epacketDdp]:
    PassportGlobalServiceCodeConstant.epacketDdp,
  [PassportGlobalServices.ExpressDdp]:
    PassportGlobalServiceCodeConstant.ExpressDdp,
  [PassportGlobalServices.PriorityDduDelcon]:
    PassportGlobalServiceCodeConstant.PriorityDduDelcon,
  [PassportGlobalServices.PriorityDdu]:
    PassportGlobalServiceCodeConstant.PriorityDdu,
  [PassportGlobalServices.ExpeditedDdpDelcon]:
    PassportGlobalServiceCodeConstant.ExpeditedDdpDelcon,
  [PassportGlobalServices.ExpressDdpDelcon]:
    PassportGlobalServiceCodeConstant.ExpressDdpDelcon,
  [PassportGlobalServices.GlobalPlus]:
    PassportGlobalServiceCodeConstant.GlobalPlus,
};

enum LandmarkGlobalServiceCodeConstant {
  Delivery = "lg_d",
}

const landmarkGlobalServiceToServiceCodeConstant: Record<
  LandmarkGlobalServices,
  LandmarkGlobalServiceCodeConstant
> = {
  [LandmarkGlobalServices.Delivery]: LandmarkGlobalServiceCodeConstant.Delivery,
};

enum ETAExpressServiceCodeConstant {
  ETAExpress = "eta_express",
}

const etaExpressServiceToServiceCodeConstant: Record<
  ETAExpressServices,
  ETAExpressServiceCodeConstant
> = {
  [ETAExpressServices.ETAExpress]: ETAExpressServiceCodeConstant.ETAExpress,
};

enum EPostGlobalServiceCodeConstant {
  CourierServiceDDP = "epg_a",
  CourierServiceDDU = "epg_b",
  DomesticEconomyParcel = "epg_c",
  DomesticParcelBPM = "epg_d",
  DomesticPriorityParcel = "epg_e",
  DomesticPriorityParcelBPM = "epg_f",
  EMIService = "epg_g",
  EconomyParcelService = "epg_h",
  IPAService = "epg_i",
  ISALService = "epg_j",
  PMIService = "epg_k",
  PriorityParcelDDP = "epg_l",
  PriorityParcelDDU = "epg_m",
  PriorityParcelDeliveryConfirmationDDP = "epg_n",
  PriorityParcelDeliveryConfirmationDDU = "epg_o",
  ePacketService = "epg_p",
}

const epostGlobalServiceToServiceCodeConstant: Record<
  EPostGlobalServices,
  EPostGlobalServiceCodeConstant
> = {
  [EPostGlobalServices.CourierServiceDDP]:
    EPostGlobalServiceCodeConstant.CourierServiceDDP,
  [EPostGlobalServices.CourierServiceDDU]:
    EPostGlobalServiceCodeConstant.CourierServiceDDU,
  [EPostGlobalServices.DomesticEconomyParcel]:
    EPostGlobalServiceCodeConstant.DomesticEconomyParcel,
  [EPostGlobalServices.DomesticParcelBPM]:
    EPostGlobalServiceCodeConstant.DomesticParcelBPM,
  [EPostGlobalServices.DomesticPriorityParcel]:
    EPostGlobalServiceCodeConstant.DomesticPriorityParcel,
  [EPostGlobalServices.DomesticPriorityParcelBPM]:
    EPostGlobalServiceCodeConstant.DomesticPriorityParcelBPM,
  [EPostGlobalServices.EMIService]: EPostGlobalServiceCodeConstant.EMIService,
  [EPostGlobalServices.EconomyParcelService]:
    EPostGlobalServiceCodeConstant.EconomyParcelService,
  [EPostGlobalServices.IPAService]: EPostGlobalServiceCodeConstant.IPAService,
  [EPostGlobalServices.ISALService]: EPostGlobalServiceCodeConstant.ISALService,
  [EPostGlobalServices.PMIService]: EPostGlobalServiceCodeConstant.PMIService,
  [EPostGlobalServices.PriorityParcelDDP]:
    EPostGlobalServiceCodeConstant.PriorityParcelDDP,
  [EPostGlobalServices.PriorityParcelDDU]:
    EPostGlobalServiceCodeConstant.PriorityParcelDDU,
  [EPostGlobalServices.PriorityParcelDeliveryConfirmationDDP]:
    EPostGlobalServiceCodeConstant.PriorityParcelDeliveryConfirmationDDP,
  [EPostGlobalServices.PriorityParcelDeliveryConfirmationDDU]:
    EPostGlobalServiceCodeConstant.PriorityParcelDeliveryConfirmationDDU,
  [EPostGlobalServices.ePacketService]:
    EPostGlobalServiceCodeConstant.ePacketService,
};

enum OnTracServiceCodeConstant {
  Ground = "ot_a",
}

const onTracServiceToServiceCodeConstant: Record<
  OnTracServices,
  OnTracServiceCodeConstant
> = { [OnTracServices.Ground]: OnTracServiceCodeConstant.Ground };

enum GlobalEServiceCodeConstant {
  Delivery = "ge_d",
}

const globalEServiceToServiceCodeConstant: Record<
  GlobalEServices,
  GlobalEServiceCodeConstant
> = { [GlobalEServices.Delivery]: GlobalEServiceCodeConstant.Delivery };

enum UpsMailInnovationsServiceCodeConstant {
  StandardFlats = "umi_a",
  ParcelSelectLightweightIrreg = "umi_b",
  ParcelSelect = "umi_c",
  BPMParcels = "umi_d",
  BPMFlats = "umi_e",
  MediaMail = "umi_f",
  MarketingParcels = "umi_g",
  EconomyMailInnovations = "umi_h",
  ExpeditedMailInnovations = "umi_i",
  First = "umi_j",
  Priority = "umi_k",
  PriorityMailInnovations = "umi_l",
  SingleReturns = "umi_m",
}

const upsMailInnovationsServiceToServiceCodeConstant: Record<
  UpsMailInnovationsServices,
  UpsMailInnovationsServiceCodeConstant
> = {
  [UpsMailInnovationsServices.StandardFlats]:
    UpsMailInnovationsServiceCodeConstant.StandardFlats,
  [UpsMailInnovationsServices.ParcelSelectLightweightIrreg]:
    UpsMailInnovationsServiceCodeConstant.ParcelSelectLightweightIrreg,
  [UpsMailInnovationsServices.ParcelSelect]:
    UpsMailInnovationsServiceCodeConstant.ParcelSelect,
  [UpsMailInnovationsServices.BPMParcels]:
    UpsMailInnovationsServiceCodeConstant.BPMParcels,
  [UpsMailInnovationsServices.BPMFlats]:
    UpsMailInnovationsServiceCodeConstant.BPMFlats,
  [UpsMailInnovationsServices.MediaMail]:
    UpsMailInnovationsServiceCodeConstant.MediaMail,
  [UpsMailInnovationsServices.MarketingParcels]:
    UpsMailInnovationsServiceCodeConstant.MarketingParcels,
  [UpsMailInnovationsServices.EconomyMailInnovations]:
    UpsMailInnovationsServiceCodeConstant.EconomyMailInnovations,
  [UpsMailInnovationsServices.ExpeditedMailInnovations]:
    UpsMailInnovationsServiceCodeConstant.ExpeditedMailInnovations,
  [UpsMailInnovationsServices.First]:
    UpsMailInnovationsServiceCodeConstant.First,
  [UpsMailInnovationsServices.Priority]:
    UpsMailInnovationsServiceCodeConstant.Priority,
  [UpsMailInnovationsServices.PriorityMailInnovations]:
    UpsMailInnovationsServiceCodeConstant.PriorityMailInnovations,
  [UpsMailInnovationsServices.SingleReturns]:
    UpsMailInnovationsServiceCodeConstant.SingleReturns,
};

enum PurolatorServiceCodeConstant {
  PurolatorExpress = "pur_a",
  PurolatorExpress12PM = "pur_b",
  PurolatorExpressPack12PM = "pur_c",
  PurolatorExpressBox12PM = "pur_d",
  PurolatorExpressEnvelope12PM = "pur_e",
  PurolatorExpress1030AM = "pur_f",
  PurolatorExpress9AM = "pur_g",
  PurolatorExpressBox = "pur_h",
  PurolatorExpressBox1030AM = "pur_i",
  PurolatorExpressBox9AM = "pur_j",
  PurolatorExpressBoxEvening = "pur_k",
  PurolatorExpressBoxInternational = "pur_l",
  PurolatorExpressBoxUS = "pur_m",
  PurolatorExpressEnvelope = "pur_n",
  PurolatorExpressEnvelope1030AM = "pur_o",
  PurolatorExpressEnvelope9AM = "pur_p",
  PurolatorExpressEnvelopeEvening = "pur_q",
  PurolatorExpressEnvelopeInternational = "pur_r",
  PurolatorExpressEnvelopeUS = "pur_s",
  PurolatorExpressEvening = "pur_t",
  PurolatorExpressInternational = "pur_u",
  PurolatorExpressInternational1030AM = "pur_v",
  PurolatorExpressInternational1200 = "pur_w",
  PurolatorExpressInternational9AM = "pur_x",
  PurolatorExpressBoxInternational1030AM = "pur_y",
  PurolatorExpressBoxInternational1200 = "pur_z",
  PurolatorExpressBoxInternational9AM = "pur_aa",
  PurolatorExpressEnvelopeInternational1030AM = "pur_ab",
  PurolatorExpressEnvelopeInternational1200 = "pur_ac",
  PurolatorExpressEnvelopeInternational9AM = "pur_ad",
  PurolatorExpressPackInternational1030AM = "pur_ae",
  PurolatorExpressPackInternational1200 = "pur_af",
  PurolatorExpressPackInternational9AM = "pur_ag",
  PurolatorExpressPack = "pur_ah",
  PurolatorExpressPack1030AM = "pur_ai",
  PurolatorExpressPack9AM = "pur_aj",
  PurolatorExpressPackEvening = "pur_ak",
  PurolatorExpressPackInternational = "pur_al",
  PurolatorExpressPackUS = "pur_am",
  PurolatorExpressUS = "pur_an",
  PurolatorExpressUS1030AM = "pur_ao",
  PurolatorExpressUS1200 = "pur_ap",
  PurolatorExpressUS9AM = "pur_aq",
  PurolatorExpressBoxUS1030AM = "pur_ar",
  PurolatorExpressBoxUS1200 = "pur_as",
  PurolatorExpressBoxUS9AM = "pur_at",
  PurolatorExpressEnvelopeUS1030AM = "pur_au",
  PurolatorExpressEnvelopeUS1200 = "pur_av",
  PurolatorExpressEnvelopeUS9AM = "pur_aw",
  PurolatorExpressPackUS1030AM = "pur_ax",
  PurolatorExpressPackUS1200 = "pur_ay",
  PurolatorExpressPackUS9AM = "pur_az",
  PurolatorGround = "pur_ba",
  PurolatorGround1030AM = "pur_bb",
  PurolatorGround9AM = "pur_bc",
  PurolatorGround12PM = "pur_bd",
  PurolatorGroundDistribution = "pur_be",
  PurolatorGroundEvening = "pur_bf",
  PurolatorGroundUS = "pur_bg",
  PurolatorQuickShip = "pur_bh",
  PurolatorQuickShipEnvelope = "pur_bi",
  PurolatorQuickShipPack = "pur_bj",
  PurolatorQuickShipBox = "pur_bk",
}

const purolatorServiceToServiceCodeConstant: Record<
  PurolatorServices,
  PurolatorServiceCodeConstant
> = {
  [PurolatorServices.PurolatorExpress]:
    PurolatorServiceCodeConstant.PurolatorExpress,
  [PurolatorServices.PurolatorExpress12PM]:
    PurolatorServiceCodeConstant.PurolatorExpress12PM,
  [PurolatorServices.PurolatorExpressPack12PM]:
    PurolatorServiceCodeConstant.PurolatorExpressPack12PM,
  [PurolatorServices.PurolatorExpressBox12PM]:
    PurolatorServiceCodeConstant.PurolatorExpressBox12PM,
  [PurolatorServices.PurolatorExpressEnvelope12PM]:
    PurolatorServiceCodeConstant.PurolatorExpressEnvelope12PM,
  [PurolatorServices.PurolatorExpress1030AM]:
    PurolatorServiceCodeConstant.PurolatorExpress1030AM,
  [PurolatorServices.PurolatorExpress9AM]:
    PurolatorServiceCodeConstant.PurolatorExpress9AM,
  [PurolatorServices.PurolatorExpressBox]:
    PurolatorServiceCodeConstant.PurolatorExpressBox,
  [PurolatorServices.PurolatorExpressBox1030AM]:
    PurolatorServiceCodeConstant.PurolatorExpressBox1030AM,
  [PurolatorServices.PurolatorExpressBox9AM]:
    PurolatorServiceCodeConstant.PurolatorExpressBox9AM,
  [PurolatorServices.PurolatorExpressBoxEvening]:
    PurolatorServiceCodeConstant.PurolatorExpressBoxEvening,
  [PurolatorServices.PurolatorExpressBoxInternational]:
    PurolatorServiceCodeConstant.PurolatorExpressBoxInternational,
  [PurolatorServices.PurolatorExpressBoxUS]:
    PurolatorServiceCodeConstant.PurolatorExpressBoxUS,
  [PurolatorServices.PurolatorExpressEnvelope]:
    PurolatorServiceCodeConstant.PurolatorExpressEnvelope,
  [PurolatorServices.PurolatorExpressEnvelope1030AM]:
    PurolatorServiceCodeConstant.PurolatorExpressEnvelope1030AM,
  [PurolatorServices.PurolatorExpressEnvelope9AM]:
    PurolatorServiceCodeConstant.PurolatorExpressEnvelope9AM,
  [PurolatorServices.PurolatorExpressEnvelopeEvening]:
    PurolatorServiceCodeConstant.PurolatorExpressEnvelopeEvening,
  [PurolatorServices.PurolatorExpressEnvelopeInternational]:
    PurolatorServiceCodeConstant.PurolatorExpressEnvelopeInternational,
  [PurolatorServices.PurolatorExpressEnvelopeUS]:
    PurolatorServiceCodeConstant.PurolatorExpressEnvelopeUS,
  [PurolatorServices.PurolatorExpressEvening]:
    PurolatorServiceCodeConstant.PurolatorExpressEvening,
  [PurolatorServices.PurolatorExpressInternational]:
    PurolatorServiceCodeConstant.PurolatorExpressInternational,
  [PurolatorServices.PurolatorExpressInternational1030AM]:
    PurolatorServiceCodeConstant.PurolatorExpressInternational1030AM,
  [PurolatorServices.PurolatorExpressInternational1200]:
    PurolatorServiceCodeConstant.PurolatorExpressInternational1200,
  [PurolatorServices.PurolatorExpressInternational9AM]:
    PurolatorServiceCodeConstant.PurolatorExpressInternational9AM,
  [PurolatorServices.PurolatorExpressBoxInternational1030AM]:
    PurolatorServiceCodeConstant.PurolatorExpressBoxInternational1030AM,
  [PurolatorServices.PurolatorExpressBoxInternational1200]:
    PurolatorServiceCodeConstant.PurolatorExpressBoxInternational1200,
  [PurolatorServices.PurolatorExpressBoxInternational9AM]:
    PurolatorServiceCodeConstant.PurolatorExpressBoxInternational9AM,
  [PurolatorServices.PurolatorExpressEnvelopeInternational1030AM]:
    PurolatorServiceCodeConstant.PurolatorExpressEnvelopeInternational1030AM,
  [PurolatorServices.PurolatorExpressEnvelopeInternational1200]:
    PurolatorServiceCodeConstant.PurolatorExpressEnvelopeInternational1200,
  [PurolatorServices.PurolatorExpressEnvelopeInternational9AM]:
    PurolatorServiceCodeConstant.PurolatorExpressEnvelopeInternational9AM,
  [PurolatorServices.PurolatorExpressPackInternational1030AM]:
    PurolatorServiceCodeConstant.PurolatorExpressPackInternational1030AM,
  [PurolatorServices.PurolatorExpressPackInternational1200]:
    PurolatorServiceCodeConstant.PurolatorExpressPackInternational1200,
  [PurolatorServices.PurolatorExpressPackInternational9AM]:
    PurolatorServiceCodeConstant.PurolatorExpressPackInternational9AM,
  [PurolatorServices.PurolatorExpressPack]:
    PurolatorServiceCodeConstant.PurolatorExpressPack,
  [PurolatorServices.PurolatorExpressPack1030AM]:
    PurolatorServiceCodeConstant.PurolatorExpressPack1030AM,
  [PurolatorServices.PurolatorExpressPack9AM]:
    PurolatorServiceCodeConstant.PurolatorExpressPack9AM,
  [PurolatorServices.PurolatorExpressPackEvening]:
    PurolatorServiceCodeConstant.PurolatorExpressPackEvening,
  [PurolatorServices.PurolatorExpressPackInternational]:
    PurolatorServiceCodeConstant.PurolatorExpressPackInternational,
  [PurolatorServices.PurolatorExpressPackUS]:
    PurolatorServiceCodeConstant.PurolatorExpressPackUS,
  [PurolatorServices.PurolatorExpressUS]:
    PurolatorServiceCodeConstant.PurolatorExpressUS,
  [PurolatorServices.PurolatorExpressUS1030AM]:
    PurolatorServiceCodeConstant.PurolatorExpressUS1030AM,
  [PurolatorServices.PurolatorExpressUS1200]:
    PurolatorServiceCodeConstant.PurolatorExpressUS1200,
  [PurolatorServices.PurolatorExpressUS9AM]:
    PurolatorServiceCodeConstant.PurolatorExpressUS9AM,
  [PurolatorServices.PurolatorExpressBoxUS1030AM]:
    PurolatorServiceCodeConstant.PurolatorExpressBoxUS1030AM,
  [PurolatorServices.PurolatorExpressBoxUS1200]:
    PurolatorServiceCodeConstant.PurolatorExpressBoxUS1200,
  [PurolatorServices.PurolatorExpressBoxUS9AM]:
    PurolatorServiceCodeConstant.PurolatorExpressBoxUS9AM,
  [PurolatorServices.PurolatorExpressEnvelopeUS1030AM]:
    PurolatorServiceCodeConstant.PurolatorExpressEnvelopeUS1030AM,
  [PurolatorServices.PurolatorExpressEnvelopeUS1200]:
    PurolatorServiceCodeConstant.PurolatorExpressEnvelopeUS1200,
  [PurolatorServices.PurolatorExpressEnvelopeUS9AM]:
    PurolatorServiceCodeConstant.PurolatorExpressEnvelopeUS9AM,
  [PurolatorServices.PurolatorExpressPackUS1030AM]:
    PurolatorServiceCodeConstant.PurolatorExpressPackUS1030AM,
  [PurolatorServices.PurolatorExpressPackUS1200]:
    PurolatorServiceCodeConstant.PurolatorExpressPackUS1200,
  [PurolatorServices.PurolatorExpressPackUS9AM]:
    PurolatorServiceCodeConstant.PurolatorExpressPackUS9AM,
  [PurolatorServices.PurolatorGround]:
    PurolatorServiceCodeConstant.PurolatorGround,
  [PurolatorServices.PurolatorGround1030AM]:
    PurolatorServiceCodeConstant.PurolatorGround1030AM,
  [PurolatorServices.PurolatorGround9AM]:
    PurolatorServiceCodeConstant.PurolatorGround9AM,
  [PurolatorServices.PurolatorGround12PM]:
    PurolatorServiceCodeConstant.PurolatorGround12PM,
  [PurolatorServices.PurolatorGroundDistribution]:
    PurolatorServiceCodeConstant.PurolatorGroundDistribution,
  [PurolatorServices.PurolatorGroundEvening]:
    PurolatorServiceCodeConstant.PurolatorGroundEvening,
  [PurolatorServices.PurolatorGroundUS]:
    PurolatorServiceCodeConstant.PurolatorGroundUS,
  [PurolatorServices.PurolatorQuickShip]:
    PurolatorServiceCodeConstant.PurolatorQuickShip,
  [PurolatorServices.PurolatorQuickShipEnvelope]:
    PurolatorServiceCodeConstant.PurolatorQuickShipEnvelope,
  [PurolatorServices.PurolatorQuickShipPack]:
    PurolatorServiceCodeConstant.PurolatorQuickShipPack,
  [PurolatorServices.PurolatorQuickShipBox]:
    PurolatorServiceCodeConstant.PurolatorQuickShipBox,
};

enum UniUniServiceCodeConstant {
  UniUniDelivery = "uu_a",
}

const uniUniServiceToServiceCodeConstant: Record<
  UniUniServices,
  UniUniServiceCodeConstant
> = { [UniUniServices.Standard]: UniUniServiceCodeConstant.UniUniDelivery };

enum StallionExpressServiceCodeConstant {
  StallionEconomyUsa = "se_r",
  StallionEconomyCa = "se_s",
  UsEconomy = "se_t",
  UsRegional = "se_u",
  PrecisionGround = "se_v",
  WhizDeliveryStandard = "se_w",
  WhizDeliveryNextDay = "se_x",
}

const stallionExpressServiceToServiceCodeConstant: Record<
  StallionExpressServices,
  StallionExpressServiceCodeConstant
> = {
  [StallionExpressServices.StallionEconomyUsa]:
    StallionExpressServiceCodeConstant.StallionEconomyUsa,
  [StallionExpressServices.StallionEconomyCa]:
    StallionExpressServiceCodeConstant.StallionEconomyCa,
  [StallionExpressServices.UsEconomy]:
    StallionExpressServiceCodeConstant.UsEconomy,
  [StallionExpressServices.UsRegional]:
    StallionExpressServiceCodeConstant.UsRegional,
  [StallionExpressServices.PrecisionGround]:
    StallionExpressServiceCodeConstant.PrecisionGround,
};

enum ChitChatsServiceCodeConstant {
  ChitChatsUsEdge = "cc_a",
  ChitChatsUsConnect = "cc_b",
  ChitChatsUsSelect = "cc_c",
  ChitChatsSelect = "cc_d",
  ChitChatsSlim = "cc_e",
  ChitChatsCanadaTracked = "cc_f",
  ChitChatsInternationalTracked = "cc_g",
}

const chitChatsServiceToServiceCodeConstant: Record<
  ChitChatsServices,
  ChitChatsServiceCodeConstant
> = {
  [ChitChatsServices.ChitChatsUsEdge]:
    ChitChatsServiceCodeConstant.ChitChatsUsEdge,
  [ChitChatsServices.ChitChatsUsConnect]:
    ChitChatsServiceCodeConstant.ChitChatsUsConnect,
  [ChitChatsServices.ChitChatsUsSelect]:
    ChitChatsServiceCodeConstant.ChitChatsUsSelect,
  [ChitChatsServices.ChitChatsSelect]:
    ChitChatsServiceCodeConstant.ChitChatsSelect,
  [ChitChatsServices.ChitChatsSlim]: ChitChatsServiceCodeConstant.ChitChatsSlim,
  [ChitChatsServices.ChitChatsCanadaTracked]:
    ChitChatsServiceCodeConstant.ChitChatsCanadaTracked,
  [ChitChatsServices.ChitChatsInternationalTracked]:
    ChitChatsServiceCodeConstant.ChitChatsInternationalTracked,
};

enum EcomExpressServiceCodeConstant {
  SameDay = "ecom_a",
  NextDay = "ecom_b",
  Standard = "ecom_c",
}

const ecomExpressServiceToServiceCodeConstant: Record<
  EcomExpressServices,
  EcomExpressServiceCodeConstant
> = {
  [EcomExpressServices.SameDay]: EcomExpressServiceCodeConstant.SameDay,
  [EcomExpressServices.NextDay]: EcomExpressServiceCodeConstant.NextDay,
  [EcomExpressServices.Standard]: EcomExpressServiceCodeConstant.Standard,
};

enum PostNLServiceCodeConstant {
  InternationalPacket = "postnl_a",
  InternationalPacketTracked = "postnl_b",
}

const postNLServiceToServiceCodeConstant: Record<
  PostNLServices,
  PostNLServiceCodeConstant
> = {
  [PostNLServices.InternationalPacket]:
    PostNLServiceCodeConstant.InternationalPacket,
  [PostNLServices.InternationalPacketTracked]:
    PostNLServiceCodeConstant.InternationalPacketTracked,
};

enum IntelcomServiceCodeConstant {
  Standard = "intelcom_a",
}

const intelcomServiceToServiceCodeConstant: Record<
  IntelcomServices,
  IntelcomServiceCodeConstant
> = { [IntelcomServices.Standard]: IntelcomServiceCodeConstant.Standard };

enum FleetOpticsServiceCodeConstant {
  Express = "fo_a",
  Ground = "fo_b",
}

const fleetOpticsServiceToServiceCodeConstant: Record<
  FleetOpticsServices,
  FleetOpticsServiceCodeConstant
> = {
  [FleetOpticsServices.Express]: FleetOpticsServiceCodeConstant.Express,
  [FleetOpticsServices.Ground]: FleetOpticsServiceCodeConstant.Ground,
};

enum IcsCourierServiceCodeConstant {
  Express = "ics_a",
  Ground = "ics_b",
}

const icsCourierServiceToServiceCodeConstant: Record<
  IcsCourierServices,
  IcsCourierServiceCodeConstant
> = {
  [IcsCourierServices.Express]: IcsCourierServiceCodeConstant.Express,
  [IcsCourierServices.Ground]: IcsCourierServiceCodeConstant.Ground,
};

enum WhizDeliveryServiceCodeConstant {
  Standard = "whiz_a",
  NextDay = "whiz_b",
}

const whizDeliveryServiceToServiceCodeConstant: Record<
  WhizDeliveryServices,
  WhizDeliveryServiceCodeConstant
> = {
  [WhizDeliveryServices.Standard]: WhizDeliveryServiceCodeConstant.Standard,
  [WhizDeliveryServices.NextDay]: WhizDeliveryServiceCodeConstant.NextDay,
};

enum TiktokShopServiceCodeConstant {
  CollectionByTiktok = "tiktok_a",
}

const tiktokShopServiceToServiceCodeConstant: Record<
  TiktokShopServices,
  TiktokShopServiceCodeConstant
> = {
  [TiktokShopServices.CollectionByTiktok]:
    TiktokShopServiceCodeConstant.CollectionByTiktok,
};

enum CanparServiceCodeConstant {
  Ground = "cnp_a",
  SelectLetter = "cnp_b",
  SelectPak = "cnp_c",
  Select = "cnp_d",
  OvernightLetter = "cnp_e",
  OvernightPak = "cnp_f",
  Overnight = "cnp_g",
  USALetter = "cnp_h",
  USAPak = "cnp_i",
  SelectUSA = "cnp_j",
}

const canparServiceToServiceCodeConstant: Record<
  CanparServices,
  CanparServiceCodeConstant
> = {
  [CanparServices.Ground]: CanparServiceCodeConstant.Ground,
  [CanparServices.SelectLetter]: CanparServiceCodeConstant.SelectLetter,
  [CanparServices.SelectPak]: CanparServiceCodeConstant.SelectPak,
  [CanparServices.Select]: CanparServiceCodeConstant.Select,
  [CanparServices.OvernightLetter]: CanparServiceCodeConstant.OvernightLetter,
  [CanparServices.OvernightPak]: CanparServiceCodeConstant.OvernightPak,
  [CanparServices.Overnight]: CanparServiceCodeConstant.Overnight,
  [CanparServices.USALetter]: CanparServiceCodeConstant.USALetter,
  [CanparServices.USAPak]: CanparServiceCodeConstant.USAPak,
  [CanparServices.SelectUSA]: CanparServiceCodeConstant.SelectUSA,
};

const RedoCarrierServiceSchema = z.union([
  z.object({
    [CARRIER_KEY]: createEnum([
      CarrierServiceCodeConstant.FEDEX,
      CarrierServiceCodeConstant.FEDEX_SMART_POST,
    ]),
    [SERVICE_KEY]: createEnum(FedexServiceCodeConstant),
  }),
  z.object({
    [CARRIER_KEY]: createEnum([
      CarrierServiceCodeConstant.UPS,
      CarrierServiceCodeConstant.UPS_SUREPOST,
    ]),
    [SERVICE_KEY]: createEnum(UpsServiceCodeConstant),
  }),
  z.object({
    [CARRIER_KEY]: createEnum([
      CarrierServiceCodeConstant.USPS,
      CarrierServiceCodeConstant.USPS_RETURNS,
      CarrierServiceCodeConstant.USPS_SHIP,
    ]),
    [SERVICE_KEY]: createEnum(UspsServiceCodeConstant),
  }),
  z.object({
    [CARRIER_KEY]: createEnum([CarrierServiceCodeConstant.AMAZON]),
    [SERVICE_KEY]: createEnum(AmazonServiceCodeConstant),
  }),
  z.object({
    [CARRIER_KEY]: createEnum([CarrierServiceCodeConstant.DHL_EXPRESS]),
    [SERVICE_KEY]: createEnum(DhlExpressServiceCodeConstant),
  }),
  z.object({
    [CARRIER_KEY]: createEnum([CarrierServiceCodeConstant.VEHO]),
    [SERVICE_KEY]: createEnum(VehoServiceCodeConstant),
  }),
  z.object({
    [CARRIER_KEY]: createEnum([CarrierServiceCodeConstant.APC]),
    [SERVICE_KEY]: createEnum(ApcServiceCodeConstant),
  }),
  z.object({
    [CARRIER_KEY]: createEnum([CarrierServiceCodeConstant.FIRST_MILE]),
    [SERVICE_KEY]: createEnum(FirstMileServiceCodeConstant),
  }),
  z.object({
    [CARRIER_KEY]: createEnum([CarrierServiceCodeConstant.DHL_ECS]),
    [SERVICE_KEY]: createEnum(DhlEcsServiceCodeConstant),
  }),
  z.object({
    [CARRIER_KEY]: createEnum([CarrierServiceCodeConstant.AUSTRALIA_POST]),
    [SERVICE_KEY]: createEnum(AustraliaPostServiceCodeConstant),
  }),
  z.object({
    [CARRIER_KEY]: createEnum([CarrierServiceCodeConstant.CANADA_POST]),
    [SERVICE_KEY]: createEnum(CanadaPostServiceCodeConstant),
  }),
  z.object({
    [CARRIER_KEY]: createEnum([CarrierServiceCodeConstant.GLS]),
    [SERVICE_KEY]: createEnum(GlsServiceCodeConstant),
  }),
  z.object({
    [CARRIER_KEY]: createEnum([CarrierServiceCodeConstant.ASENDIA]),
    [SERVICE_KEY]: createEnum(AsendiaServiceCodeConstant),
  }),
  z.object({
    [CARRIER_KEY]: createEnum([CarrierServiceCodeConstant.ROYAL_MAIL_V3]),
    [SERVICE_KEY]: createEnum(RoyalMailV3ServiceCodeConstant),
  }),
  z.object({
    [CARRIER_KEY]: createEnum([CarrierServiceCodeConstant.SPEE_DEE]),
    [SERVICE_KEY]: createEnum(SpeeDeeServiceCodeConstant),
  }),
  z.object({
    [CARRIER_KEY]: createEnum([CarrierServiceCodeConstant.PASSPORT_GLOBAL]),
    [SERVICE_KEY]: createEnum(PassportGlobalServiceCodeConstant),
  }),
  z.object({
    [CARRIER_KEY]: createEnum([CarrierServiceCodeConstant.LANDMARK_GLOBAL]),
    [SERVICE_KEY]: createEnum(LandmarkGlobalServiceCodeConstant),
  }),
  z.object({
    [CARRIER_KEY]: createEnum([CarrierServiceCodeConstant.ETA_EXPRESS]),
    [SERVICE_KEY]: createEnum(ETAExpressServiceCodeConstant),
  }),
  z.object({
    [CARRIER_KEY]: createEnum([CarrierServiceCodeConstant.EPOST_GLOBAL]),
    [SERVICE_KEY]: createEnum(EPostGlobalServiceCodeConstant),
  }),
  z.object({
    [CARRIER_KEY]: createEnum([CarrierServiceCodeConstant.ONTRAC]),
    [SERVICE_KEY]: createEnum(OnTracServiceCodeConstant),
  }),
  z.object({
    [CARRIER_KEY]: createEnum([CarrierServiceCodeConstant.GLOBALE]),
    [SERVICE_KEY]: createEnum(GlobalEServiceCodeConstant),
  }),
  z.object({
    [CARRIER_KEY]: createEnum([
      CarrierServiceCodeConstant.UPS_MAIL_INNOVATIONS,
    ]),
    [SERVICE_KEY]: createEnum(UpsMailInnovationsServiceCodeConstant),
  }),
  z.object({
    [CARRIER_KEY]: createEnum([CarrierServiceCodeConstant.PUROLATOR]),
    [SERVICE_KEY]: createEnum(PurolatorServiceCodeConstant),
  }),
  z.object({
    [CARRIER_KEY]: createEnum([CarrierServiceCodeConstant.UNIUNI]),
    [SERVICE_KEY]: createEnum(UniUniServiceCodeConstant),
  }),
  z.object({
    [CARRIER_KEY]: createEnum([CarrierServiceCodeConstant.STALLION_EXPRESS]),
    [SERVICE_KEY]: createEnum(StallionExpressServiceCodeConstant),
  }),
  z.object({
    [CARRIER_KEY]: createEnum([CarrierServiceCodeConstant.CHITCHATS]),
    [SERVICE_KEY]: createEnum(ChitChatsServiceCodeConstant),
  }),
  z.object({
    [CARRIER_KEY]: createEnum([CarrierServiceCodeConstant.ECOM_EXPRESS]),
    [SERVICE_KEY]: createEnum(EcomExpressServiceCodeConstant),
  }),
  z.object({
    [CARRIER_KEY]: createEnum([CarrierServiceCodeConstant.POST_NL]),
    [SERVICE_KEY]: createEnum(PostNLServiceCodeConstant),
  }),
  z.object({
    [CARRIER_KEY]: createEnum([CarrierServiceCodeConstant.INTELCOM]),
    [SERVICE_KEY]: createEnum(IntelcomServiceCodeConstant),
  }),
  z.object({
    [CARRIER_KEY]: createEnum([CarrierServiceCodeConstant.FLEET_OPTICS]),
    [SERVICE_KEY]: createEnum(FleetOpticsServiceCodeConstant),
  }),
  z.object({
    [CARRIER_KEY]: createEnum([CarrierServiceCodeConstant.ICS_COURIER]),
    [SERVICE_KEY]: createEnum(IcsCourierServiceCodeConstant),
  }),
  z.object({
    [CARRIER_KEY]: createEnum([CarrierServiceCodeConstant.WHIZ_DELIVERY]),
    [SERVICE_KEY]: createEnum(WhizDeliveryServiceCodeConstant),
  }),
  z.object({
    [CARRIER_KEY]: createEnum([CarrierServiceCodeConstant.TIKTOK_SHOP]),
    [SERVICE_KEY]: createEnum(TiktokShopServiceCodeConstant),
  }),
  z.object({
    [CARRIER_KEY]: createEnum([CarrierServiceCodeConstant.CANPAR_EXPRESS]),
    [SERVICE_KEY]: createEnum(CanparServiceCodeConstant),
  }),
]);

type RedoCarrierService = z.infer<typeof RedoCarrierServiceSchema>;

// Type check to ensure all CarrierServiceCodeConstant values are represented in RedoCarrierServiceSchema
// export to consider variable "used"
export const _carrierServiceCodeConstantsAreExhaustive: Exclude<
  CarrierServiceCodeConstant,
  RedoCarrierService[typeof CARRIER_KEY]
> extends never
  ? true
  : never = true;

// ============================================================================
// NEW COMPACT SERVICE CODE SYSTEM (using checkout-shipping-options collection)
// ============================================================================
//
// Everything below this line is for the new system that stores detailed
// shipping option data in the checkout-shipping-options TTL collection and
// uses compact service codes that only contain essential identifiers.
//
// The compact service code format is:
// {
//   "p": "redo",                 // provider
//   "d": "documentId",           // TTL document ID
//   "r": "rateTableId",          // rate table ID
//   "n": "nodeId",               // checkout tree node ID
//   "t": "treatmentId",          // treatment ID (optional)
//   "dd": "promisedDeliveryDate" // promised delivery date (optional)
// }

// CompactServiceCodeKey is imported from ./service-code-lean (single source of
// truth, shared with the checkout-bundle parser).

export const compactServiceCodeSchema = z.object({
  [CompactServiceCodeKey.PROVIDER]: z.literal("redo"),
  [CompactServiceCodeKey.DOCUMENT_ID]: zExt.objectId(),
  [CompactServiceCodeKey.RATE_TABLE_ID]: zExt.objectId(),
  [CompactServiceCodeKey.NODE_ID]: z.string(),
  [CompactServiceCodeKey.TREATMENT_ID]: z.string().optional(),
  [CompactServiceCodeKey.PROMISED_DELIVERY_DATE]: z.string().optional(),
  /**
   * Included so merchants can map the selected carrier service to the label
   * they should purchase in their OMS.
   */
  [CompactServiceCodeKey.CARRIER_SERVICE_KEY]: z.string().optional(),
  /**
   * Indicates the shipping option is being previewed.
   */
  [CompactServiceCodeKey.PREVIEW_MODE]: z.boolean().optional(),
  /**
   * Indicates if the redo coverage is included in the shipping option.
   */
  [CompactServiceCodeKey.REDO_COVERAGE]: z.boolean().optional(),
  /**
   * Shipping portion of the displayed rate in presentment currency, encoded
   * in minor units (integer). Includes carrier cost + markup; excludes DTF
   * (duties, taxes, fees). Used by Shopify discount functions to bound
   * shipping discounts so landed cost is preserved.
   */
  [CompactServiceCodeKey.SHIPPING_COST]: z
    .number()
    .int()
    .nonnegative()
    .optional(),
  [CompactServiceCodeKey.LANDED_COST]: z
    .number()
    .int()
    .nonnegative()
    .optional(),
  [CompactServiceCodeKey.SHOP_DECIMALS]: z
    .number()
    .int()
    .min(0)
    .max(MAX_CURRENCY_DECIMALS)
    .optional(),
});

export type CompactServiceCode = z.infer<typeof compactServiceCodeSchema>;

/**
 * Creates a compact service code that references a TTL document
 */
export function createCompactServiceCode(params: {
  documentId: string;
  rateTableId: string;
  nodeId: string;
  treatmentId?: string;
  promisedDeliveryDate?: PromisedDeliveryDate;
  carrierService:
    | { shouldInclude: true; value: CorrelatedCarrierService }
    | { shouldInclude: false };
  previewMode?: boolean;
  redoCoverage?: boolean;
  /**
   * Shipping portion of the displayed rate in presentment currency, in minor
   * units (integer cents). Includes markup, excludes DTF. See
   * {@link CompactServiceCodeKey.SHIPPING_COST}.
   */
  shippingCost?: number;
  landedCost?: number;
  shopDecimals?: number;
}): string {
  const serviceCode: CompactServiceCode = {
    [CompactServiceCodeKey.PROVIDER]: "redo",
    [CompactServiceCodeKey.DOCUMENT_ID]: params.documentId,
    [CompactServiceCodeKey.RATE_TABLE_ID]: params.rateTableId,
    [CompactServiceCodeKey.NODE_ID]: params.nodeId,
  };

  if (params.carrierService.shouldInclude) {
    serviceCode[CompactServiceCodeKey.CARRIER_SERVICE_KEY] =
      carrierServiceToRedoCarrierServiceServiceKey(
        params.carrierService.value.service,
      );
  }

  if (params.treatmentId) {
    serviceCode[CompactServiceCodeKey.TREATMENT_ID] = params.treatmentId;
  }

  if (params.promisedDeliveryDate) {
    serviceCode[CompactServiceCodeKey.PROMISED_DELIVERY_DATE] =
      serializePromisedDeliveryDate(params.promisedDeliveryDate);
  }

  if (params.previewMode) {
    serviceCode[CompactServiceCodeKey.PREVIEW_MODE] = true;
  }

  if (params.redoCoverage) {
    serviceCode[CompactServiceCodeKey.REDO_COVERAGE] = params.redoCoverage;
  }

  if (params.shippingCost !== undefined) {
    serviceCode[CompactServiceCodeKey.SHIPPING_COST] = params.shippingCost;
  }

  if (params.landedCost !== undefined) {
    serviceCode[CompactServiceCodeKey.LANDED_COST] = params.landedCost;
  }

  if (params.shopDecimals !== undefined) {
    serviceCode[CompactServiceCodeKey.SHOP_DECIMALS] = params.shopDecimals;
  }

  return JSON.stringify(compactServiceCodeSchema.parse(serviceCode));
}

function carrierServiceToRedoCarrierServiceServiceKey(
  carrierService: CorrelatedCarrierService["service"],
): RedoCarrierService[typeof SERVICE_KEY] {
  if (isFedexService(carrierService)) {
    return fedexServiceToServiceCodeConstant[carrierService];
  } else if (isUpsService(carrierService)) {
    return upsServiceToServiceCodeConstant[carrierService];
  } else if (isAmazonService(carrierService)) {
    return amazonServiceToServiceCodeConstant[carrierService];
  } else if (isUspsService(carrierService)) {
    return uspsServiceToServiceCodeConstant[carrierService];
  } else if (isDhlExpressService(carrierService)) {
    return dhlExpressServiceToServiceCodeConstant[carrierService];
  } else if (isVehoService(carrierService)) {
    return vehoServiceToServiceCodeConstant[carrierService];
  } else if (isApcService(carrierService)) {
    return apcServiceToServiceCodeConstant[carrierService];
  } else if (isFirstMileService(carrierService)) {
    return firstMileServiceToServiceCodeConstant[carrierService];
  } else if (isAustraliaPostService(carrierService)) {
    return australiaPostServiceToServiceCodeConstant[carrierService];
  } else if (isCanadaPostService(carrierService)) {
    return canadaPostServiceToServiceCodeConstant[carrierService];
  } else if (isDhlEcsService(carrierService)) {
    return dhlEcsServiceToServiceCodeConstant[carrierService];
  } else if (isGlsService(carrierService)) {
    return glsServiceToServiceCodeConstant[carrierService];
  } else if (isAsendiaService(carrierService)) {
    return asendiaServiceToServiceCodeConstant[carrierService];
  } else if (isRoyalMailV3Service(carrierService)) {
    return royalMailV3ServiceToServiceCodeConstant[carrierService];
  } else if (isSpeeDeeService(carrierService)) {
    return speeDeeServiceToServiceCodeConstant[carrierService];
  } else if (isPassportGlobalService(carrierService)) {
    return passportGlobalServiceToServiceCodeConstant[carrierService];
  } else if (isLandmarkGlobalService(carrierService)) {
    return landmarkGlobalServiceToServiceCodeConstant[carrierService];
  } else if (isETAExpressService(carrierService)) {
    return etaExpressServiceToServiceCodeConstant[carrierService];
  } else if (isEPostGlobalService(carrierService)) {
    return epostGlobalServiceToServiceCodeConstant[carrierService];
  } else if (isOnTracService(carrierService)) {
    return onTracServiceToServiceCodeConstant[carrierService];
  } else if (isGlobalEService(carrierService)) {
    return globalEServiceToServiceCodeConstant[carrierService];
  } else if (isUpsMailInnovationsService(carrierService)) {
    return upsMailInnovationsServiceToServiceCodeConstant[carrierService];
  } else if (isPurolatorService(carrierService)) {
    return purolatorServiceToServiceCodeConstant[carrierService];
  } else if (isUniUniService(carrierService)) {
    return uniUniServiceToServiceCodeConstant[carrierService];
  } else if (isStallionExpressService(carrierService)) {
    return stallionExpressServiceToServiceCodeConstant[carrierService];
  } else if (isChitChatsService(carrierService)) {
    return chitChatsServiceToServiceCodeConstant[carrierService];
  } else if (isEcomExpressService(carrierService)) {
    return ecomExpressServiceToServiceCodeConstant[carrierService];
  } else if (isPostNLService(carrierService)) {
    return postNLServiceToServiceCodeConstant[carrierService];
  } else if (isIntelcomService(carrierService)) {
    return intelcomServiceToServiceCodeConstant[carrierService];
  } else if (isFleetOpticsService(carrierService)) {
    return fleetOpticsServiceToServiceCodeConstant[carrierService];
  } else if (isIcsCourierService(carrierService)) {
    return icsCourierServiceToServiceCodeConstant[carrierService];
  } else if (isWhizDeliveryService(carrierService)) {
    return whizDeliveryServiceToServiceCodeConstant[carrierService];
  } else if (isTiktokShopService(carrierService)) {
    return tiktokShopServiceToServiceCodeConstant[carrierService];
  } else if (isCanparService(carrierService)) {
    return canparServiceToServiceCodeConstant[carrierService];
  } else {
    assertNever(carrierService);
  }
}

export interface CompactServiceCodeParsedInfo {
  documentId: string;
  rateTableId: string;
  nodeId: string;
  treatmentId?: string;
  promisedDeliveryDate?: PromisedDeliveryDate;
  shippingCost?: number;
  landedCost?: number;
  shopDecimals?: number;
}

/**
 * Parses a compact service code to extract TTL document lookup information
 */
function parseCompactServiceCode(
  serviceCode: string,
): CompactServiceCodeParsedInfo | null {
  try {
    const parsed = JSON.parse(serviceCode);

    // Validate using Zod schema
    const validation = compactServiceCodeSchema.safeParse(parsed);
    if (!validation.success) {
      return null;
    }

    const data = validation.data;

    const promisedDeliveryDate = data[
      CompactServiceCodeKey.PROMISED_DELIVERY_DATE
    ]
      ? parsePromisedDeliveryDate(
          data[CompactServiceCodeKey.PROMISED_DELIVERY_DATE],
        )
      : undefined;

    return {
      documentId: data[CompactServiceCodeKey.DOCUMENT_ID],
      rateTableId: data[CompactServiceCodeKey.RATE_TABLE_ID],
      nodeId: data[CompactServiceCodeKey.NODE_ID],
      treatmentId: data[CompactServiceCodeKey.TREATMENT_ID],
      promisedDeliveryDate,
      shippingCost: data[CompactServiceCodeKey.SHIPPING_COST],
      landedCost: data[CompactServiceCodeKey.LANDED_COST],
      shopDecimals: data[CompactServiceCodeKey.SHOP_DECIMALS],
    };
  } catch {
    return null;
  }
}

/**
 * Determines if a service code is using the new compact format
 */
export function isCompactServiceCode(serviceCode: string): boolean {
  try {
    const parsed = JSON.parse(serviceCode);
    const validation = compactServiceCodeSchema.safeParse(parsed);
    return validation.success;
  } catch {
    return false;
  }
}

const RedoDynamicRateServiceCodeSchema = z.object({
  [PROVIDER_KEY]: z.literal("redo"),
  [RATE_TABLE_ID_KEY]: zExt.objectId().optional(),
  [CHECKOUT_TREE_NODE_ID_KEY]: z.string(),
  [CARRIER_SERVICE_KEY]: RedoCarrierServiceSchema,
  [COST_BREAKDOWN_KEY]: z.object({
    [COST_CURRENCY_KEY]: createEnum(Currency),
    [LABEL_COST_KEY]: z.string(),
    [MARKUP_KEY]: z
      .object({
        [MARKUP_PERCENTAGE_KEY]: z.string().optional(),
        [MARKUP_AMOUNT_KEY]: z.string().optional(),
      })
      .optional(),
    [ROUNDING_KEY]: z.string().optional(),
  }),
  [ASSIGNED_TREATMENT_IDS_KEY]: z.array(z.string()).optional(),
  [PROMISED_DELIVERY_DATE_IN_YYYYMMDD_KEY]: z.string().optional(),
});

const RedoFlatRateServiceCodeSchema = z.object({
  [PROVIDER_KEY]: z.literal("redo"),
  [RATE_TABLE_ID_KEY]: zExt.objectId(),
  [CHECKOUT_TREE_NODE_ID_KEY]: z.string(),
  [ASSIGNED_TREATMENT_IDS_KEY]: z.array(z.string()).optional(),
  [CompactServiceCodeKey.DOCUMENT_ID]: z.string().optional(),
  [CompactServiceCodeKey.PREVIEW_MODE]: z.boolean().optional(),
  [CompactServiceCodeKey.REDO_COVERAGE]: z.boolean().optional(),
});
type RedoFlatRateServiceCode = z.infer<typeof RedoFlatRateServiceCodeSchema>;

export interface PromisedDeliveryDate {
  date: Temporal.ZonedDateTime;
  visibleToCustomer: boolean;
}

export interface DynamicRateServiceCodeParams {
  rateTableId: string;
  checkoutTreeNodeId: string;
  checkoutExperienceId?: string | undefined;
  /**
   * @deprecated Use `carrierServices` instead
   */
  carrierService: CorrelatedCarrierService;
  /**
   * Multiple parcels can cause a rate table to have multiple carrier services
   */
  carrierServices?: CorrelatedCarrierService[];
  pricing: {
    labelCost: string;
    currency: Currency;
    markup?:
      | { percentage?: string | undefined; amount?: string | undefined }
      | undefined;
    rounding?: DynamicRateRounding | undefined;
  };
  assignedTreatmentIds?: string[] | undefined;
  promisedDeliveryDate: PromisedDeliveryDate;
  coverageTypes?: { return?: boolean; packageProtection?: boolean };
}

export function getFixedRateServiceCode({
  rateTableId,
  assignedTreatmentIds,
  checkoutTreeNodeId,
  documentId,
  previewMode,
  redoCoverage,
}: {
  rateTableId: string;
  assignedTreatmentIds?: string[] | undefined;
  checkoutTreeNodeId: string;
  documentId?: string | undefined;
  previewMode?: boolean | undefined;
  redoCoverage?: boolean | undefined;
}): string {
  if (rateTableId === FIXED_TROJAN_HORSE_RATE_TABLE._id) {
    return FIXED_TROJAN_HORSE_RATE_TABLE.code;
  } else if (rateTableId === SPECIAL_BLOCK_CHECKOUT_RATE_TABLE_ID) {
    return SPECIAL_BLOCK_CHECKOUT_SHIPPING_METHOD_CODE;
  }

  const serviceCodeObj: RedoFlatRateServiceCode = {
    [PROVIDER_KEY]: "redo",
    [RATE_TABLE_ID_KEY]: rateTableId,
    [CHECKOUT_TREE_NODE_ID_KEY]: checkoutTreeNodeId,
    // Force the assumption that there is only ever one test running at a time
    // so the service code does not exceed the 255 character limit.
    [ASSIGNED_TREATMENT_IDS_KEY]: assignedTreatmentIds?.[0]
      ? [assignedTreatmentIds[0]]
      : [],
    [CompactServiceCodeKey.DOCUMENT_ID]: documentId,
    [CompactServiceCodeKey.REDO_COVERAGE]: redoCoverage,
  };

  if (previewMode) {
    serviceCodeObj[CompactServiceCodeKey.PREVIEW_MODE] = true;
  }

  if (redoCoverage) {
    serviceCodeObj[CompactServiceCodeKey.REDO_COVERAGE] = redoCoverage;
  }

  return JSON.stringify(serviceCodeObj);
}

/**
 * @returns The parsed RedoServiceCode object, or null if the code is not valid
 * or not from the Redo provider.
 */
function parseDynamicShippingMethodCode(
  code: string,
): {
  provider: "redo";
  rateTableId?: string | undefined;
  carrierService: CorrelatedCarrierService;
  costBreakdown: {
    currency: Currency;
    labelCost: string;
    markup?:
      | { percentage?: string | undefined; amount?: string | undefined }
      | undefined;
    rounding?: DynamicRateRounding | undefined;
  };
  assignedTreatmentIds?: string[] | undefined;
  promisedDeliveryDate?: PromisedDeliveryDate | undefined;
} | null {
  try {
    const parsed = JSON.parse(code);
    const redoServiceCode = RedoDynamicRateServiceCodeSchema.parse(parsed);

    const carrierServiceCodeConstant =
      redoServiceCode[CARRIER_SERVICE_KEY][CARRIER_KEY];
    const serviceServiceCodeConstant =
      redoServiceCode[CARRIER_SERVICE_KEY][SERVICE_KEY];

    let service: Service | undefined;

    switch (carrierServiceCodeConstant) {
      case CarrierServiceCodeConstant.FEDEX:
      case CarrierServiceCodeConstant.FEDEX_SMART_POST: {
        const serviceString = Object.entries(
          fedexServiceToServiceCodeConstant,
        ).find(([_, value]) => value === serviceServiceCodeConstant)?.[0];
        if (serviceString && isFedexService(serviceString)) {
          service = serviceString;
        }
        break;
      }
      case CarrierServiceCodeConstant.UPS:
      case CarrierServiceCodeConstant.UPS_SUREPOST: {
        const serviceString = Object.entries(
          upsServiceToServiceCodeConstant,
        ).find(([_, value]) => value === serviceServiceCodeConstant)?.[0];
        if (serviceString && isUpsService(serviceString)) {
          service = serviceString;
        }
        break;
      }
      case CarrierServiceCodeConstant.AMAZON: {
        const serviceString = Object.entries(
          amazonServiceToServiceCodeConstant,
        ).find(([_, value]) => value === serviceServiceCodeConstant)?.[0];
        if (serviceString && isAmazonService(serviceString)) {
          service = serviceString;
        }
        break;
      }
      case CarrierServiceCodeConstant.USPS:
      case CarrierServiceCodeConstant.USPS_RETURNS:
      case CarrierServiceCodeConstant.USPS_SHIP: {
        const serviceString = Object.entries(
          uspsServiceToServiceCodeConstant,
        ).find(([_, value]) => value === serviceServiceCodeConstant)?.[0];
        if (serviceString && isUspsService(serviceString)) {
          service = serviceString;
        }
        break;
      }
      case CarrierServiceCodeConstant.DHL_EXPRESS: {
        const serviceString = Object.entries(
          dhlExpressServiceToServiceCodeConstant,
        ).find(([_, value]) => value === serviceServiceCodeConstant)?.[0];
        if (serviceString && isDhlExpressService(serviceString)) {
          service = serviceString;
        }
        break;
      }
      case CarrierServiceCodeConstant.VEHO: {
        const serviceString = Object.entries(
          vehoServiceToServiceCodeConstant,
        ).find(([_, value]) => value === serviceServiceCodeConstant)?.[0];
        if (serviceString && isVehoService(serviceString)) {
          service = serviceString;
        }
        break;
      }
      case CarrierServiceCodeConstant.APC: {
        const serviceString = Object.entries(
          apcServiceToServiceCodeConstant,
        ).find(([_, value]) => value === serviceServiceCodeConstant)?.[0];
        if (serviceString && isApcService(serviceString)) {
          service = serviceString;
        }
        break;
      }
      case CarrierServiceCodeConstant.FIRST_MILE: {
        const serviceString = Object.entries(
          firstMileServiceToServiceCodeConstant,
        ).find(([_, value]) => value === serviceServiceCodeConstant)?.[0];
        if (serviceString && isFirstMileService(serviceString)) {
          service = serviceString;
        }
        break;
      }
      case CarrierServiceCodeConstant.AUSTRALIA_POST: {
        const serviceString = Object.entries(
          australiaPostServiceToServiceCodeConstant,
        ).find(([_, value]) => value === serviceServiceCodeConstant)?.[0];
        if (serviceString && isAustraliaPostService(serviceString)) {
          service = serviceString;
        }
        break;
      }
      case CarrierServiceCodeConstant.CANADA_POST: {
        const serviceString = Object.entries(
          canadaPostServiceToServiceCodeConstant,
        ).find(([_, value]) => value === serviceServiceCodeConstant)?.[0];
        if (serviceString && isCanadaPostService(serviceString)) {
          service = serviceString;
        }
        break;
      }
      case CarrierServiceCodeConstant.DHL_ECS: {
        const serviceString = Object.entries(
          dhlEcsServiceToServiceCodeConstant,
        ).find(([_, value]) => value === serviceServiceCodeConstant)?.[0];
        if (serviceString && isDhlEcsService(serviceString)) {
          service = serviceString;
        }
        break;
      }
      case CarrierServiceCodeConstant.GLS: {
        const serviceString = Object.entries(
          glsServiceToServiceCodeConstant,
        ).find(([_, value]) => value === serviceServiceCodeConstant)?.[0];
        if (serviceString && isGlsService(serviceString)) {
          service = serviceString;
        }
        break;
      }
      case CarrierServiceCodeConstant.ASENDIA: {
        const serviceString = Object.entries(
          asendiaServiceToServiceCodeConstant,
        ).find(([_, value]) => value === serviceServiceCodeConstant)?.[0];
        if (serviceString && isAsendiaService(serviceString)) {
          service = serviceString;
        }
        break;
      }
      case CarrierServiceCodeConstant.ROYAL_MAIL_V3: {
        const serviceString = Object.entries(
          royalMailV3ServiceToServiceCodeConstant,
        ).find(([_, value]) => value === serviceServiceCodeConstant)?.[0];
        if (serviceString && isRoyalMailV3Service(serviceString)) {
          service = serviceString;
        }
        break;
      }
      case CarrierServiceCodeConstant.SPEE_DEE: {
        const serviceString = Object.entries(
          speeDeeServiceToServiceCodeConstant,
        ).find(([_, value]) => value === serviceServiceCodeConstant)?.[0];
        if (serviceString && isSpeeDeeService(serviceString)) {
          service = serviceString;
        }
        break;
      }
      case CarrierServiceCodeConstant.PASSPORT_GLOBAL: {
        const serviceString = Object.entries(
          passportGlobalServiceToServiceCodeConstant,
        ).find(([_, value]) => value === serviceServiceCodeConstant)?.[0];
        if (serviceString && isPassportGlobalService(serviceString)) {
          service = serviceString;
        }
        break;
      }
      case CarrierServiceCodeConstant.LANDMARK_GLOBAL: {
        const serviceString = Object.entries(
          landmarkGlobalServiceToServiceCodeConstant,
        ).find(([_, value]) => value === serviceServiceCodeConstant)?.[0];
        if (serviceString && isLandmarkGlobalService(serviceString)) {
          service = serviceString;
        }
        break;
      }
      case CarrierServiceCodeConstant.ETA_EXPRESS: {
        const serviceString = Object.entries(
          etaExpressServiceToServiceCodeConstant,
        ).find(([_, value]) => value === serviceServiceCodeConstant)?.[0];
        if (serviceString && isETAExpressService(serviceString)) {
          service = serviceString;
        }
        break;
      }
      case CarrierServiceCodeConstant.EPOST_GLOBAL: {
        const serviceString = Object.entries(
          epostGlobalServiceToServiceCodeConstant,
        ).find(([_, value]) => value === serviceServiceCodeConstant)?.[0];
        if (serviceString && isEPostGlobalService(serviceString)) {
          service = serviceString;
        }
        break;
      }
      case CarrierServiceCodeConstant.ONTRAC: {
        const serviceString = Object.entries(
          onTracServiceToServiceCodeConstant,
        ).find(([_, value]) => value === serviceServiceCodeConstant)?.[0];
        if (serviceString && isOnTracService(serviceString)) {
          service = serviceString;
        }
        break;
      }
      case CarrierServiceCodeConstant.GLOBALE: {
        const serviceString = Object.entries(
          globalEServiceToServiceCodeConstant,
        ).find(([_, value]) => value === serviceServiceCodeConstant)?.[0];
        if (serviceString && isGlobalEService(serviceString)) {
          service = serviceString;
        }
        break;
      }
      case CarrierServiceCodeConstant.UPS_MAIL_INNOVATIONS: {
        const serviceString = Object.entries(
          upsMailInnovationsServiceToServiceCodeConstant,
        ).find(([_, value]) => value === serviceServiceCodeConstant)?.[0];
        if (serviceString && isUpsMailInnovationsService(serviceString)) {
          service = serviceString;
        }
        break;
      }
      case CarrierServiceCodeConstant.PUROLATOR: {
        const serviceString = Object.entries(
          purolatorServiceToServiceCodeConstant,
        ).find(([_, value]) => value === serviceServiceCodeConstant)?.[0];
        if (serviceString && isPurolatorService(serviceString)) {
          service = serviceString;
        }
        break;
      }
      case CarrierServiceCodeConstant.UNIUNI: {
        const serviceString = Object.entries(
          uniUniServiceToServiceCodeConstant,
        ).find(([_, value]) => value === serviceServiceCodeConstant)?.[0];
        if (serviceString && isUniUniService(serviceString)) {
          service = serviceString;
        }
        break;
      }
      case CarrierServiceCodeConstant.STALLION_EXPRESS: {
        const serviceString = Object.entries(
          stallionExpressServiceToServiceCodeConstant,
        ).find(([_, value]) => value === serviceServiceCodeConstant)?.[0];
        if (serviceString && isStallionExpressService(serviceString)) {
          service = serviceString;
        }
        break;
      }
      case CarrierServiceCodeConstant.CHITCHATS: {
        const serviceString = Object.entries(
          chitChatsServiceToServiceCodeConstant,
        ).find(([_, value]) => value === serviceServiceCodeConstant)?.[0];
        if (serviceString && isChitChatsService(serviceString)) {
          service = serviceString;
        }
        break;
      }
      case CarrierServiceCodeConstant.ECOM_EXPRESS: {
        const serviceString = Object.entries(
          ecomExpressServiceToServiceCodeConstant,
        ).find(([_, value]) => value === serviceServiceCodeConstant)?.[0];
        if (serviceString && isEcomExpressService(serviceString)) {
          service = serviceString;
        }
        break;
      }
      case CarrierServiceCodeConstant.POST_NL: {
        const serviceString = Object.entries(
          postNLServiceToServiceCodeConstant,
        ).find(([_, value]) => value === serviceServiceCodeConstant)?.[0];
        if (serviceString && isPostNLService(serviceString)) {
          service = serviceString;
        }
        break;
      }
      case CarrierServiceCodeConstant.INTELCOM: {
        const serviceString = Object.entries(
          intelcomServiceToServiceCodeConstant,
        ).find(([_, value]) => value === serviceServiceCodeConstant)?.[0];
        if (serviceString && isIntelcomService(serviceString)) {
          service = serviceString;
        }
        break;
      }
      case CarrierServiceCodeConstant.FLEET_OPTICS: {
        const serviceString = Object.entries(
          fleetOpticsServiceToServiceCodeConstant,
        ).find(([_, value]) => value === serviceServiceCodeConstant)?.[0];
        if (serviceString && isFleetOpticsService(serviceString)) {
          service = serviceString;
        }
        break;
      }
      case CarrierServiceCodeConstant.WHIZ_DELIVERY: {
        const serviceString = Object.entries(
          whizDeliveryServiceToServiceCodeConstant,
        ).find(([_, value]) => value === serviceServiceCodeConstant)?.[0];
        if (serviceString && isWhizDeliveryService(serviceString)) {
          service = serviceString;
        }
        break;
      }
      case CarrierServiceCodeConstant.ICS_COURIER: {
        const serviceString = Object.entries(
          icsCourierServiceToServiceCodeConstant,
        ).find(([_, value]) => value === serviceServiceCodeConstant)?.[0];
        if (serviceString && isIcsCourierService(serviceString)) {
          service = serviceString;
        }
        break;
      }
      case CarrierServiceCodeConstant.TIKTOK_SHOP: {
        const serviceString = Object.entries(
          tiktokShopServiceToServiceCodeConstant,
        ).find(([_, value]) => value === serviceServiceCodeConstant)?.[0];
        if (serviceString && isTiktokShopService(serviceString)) {
          service = serviceString;
        }
        break;
      }
      case CarrierServiceCodeConstant.CANPAR_EXPRESS: {
        const serviceString = Object.entries(
          canparServiceToServiceCodeConstant,
        ).find(([_, value]) => value === serviceServiceCodeConstant)?.[0];
        if (serviceString && isCanparService(serviceString)) {
          service = serviceString;
        }
        break;
      }
      default:
        assertNever(carrierServiceCodeConstant);
    }

    if (!service) {
      throw new Error("Invalid service");
    }

    let carrier: Carriers | undefined;
    switch (carrierServiceCodeConstant) {
      case CarrierServiceCodeConstant.FEDEX:
        carrier = Carriers.FEDEX;
        break;
      case CarrierServiceCodeConstant.FEDEX_SMART_POST:
        carrier = Carriers.FEDEX_SMART_POST;
        break;
      case CarrierServiceCodeConstant.UPS:
        carrier = Carriers.UPS;
        break;
      case CarrierServiceCodeConstant.UPS_SUREPOST:
        carrier = Carriers.UPS_SUREPOST;
        break;
      case CarrierServiceCodeConstant.USPS:
        carrier = Carriers.USPS;
        break;
      case CarrierServiceCodeConstant.USPS_RETURNS:
        carrier = Carriers.USPS_RETURNS;
        break;
      case CarrierServiceCodeConstant.USPS_SHIP:
        carrier = Carriers.USPS_SHIP;
        break;
      case CarrierServiceCodeConstant.AMAZON:
        carrier = Carriers.AMAZON;
        break;
      case CarrierServiceCodeConstant.DHL_EXPRESS:
        carrier = Carriers.DHL_EXPRESS;
        break;
      case CarrierServiceCodeConstant.VEHO:
        carrier = Carriers.VEHO;
        break;
      case CarrierServiceCodeConstant.APC:
        carrier = Carriers.APC;
        break;
      case CarrierServiceCodeConstant.FIRST_MILE:
        carrier = Carriers.FIRST_MILE;
        break;
      case CarrierServiceCodeConstant.AUSTRALIA_POST:
        carrier = Carriers.AUSTRALIA_POST;
        break;
      case CarrierServiceCodeConstant.CANADA_POST:
        carrier = Carriers.CANADA_POST;
        break;
      case CarrierServiceCodeConstant.DHL_ECS:
        carrier = Carriers.DHL_ECS;
        break;
      case CarrierServiceCodeConstant.GLS:
        carrier = Carriers.GLS;
        break;
      case CarrierServiceCodeConstant.ASENDIA:
        carrier = Carriers.ASENDIA;
        break;
      case CarrierServiceCodeConstant.ROYAL_MAIL_V3:
        carrier = Carriers.ROYAL_MAIL_V3;
        break;
      case CarrierServiceCodeConstant.SPEE_DEE:
        carrier = Carriers.SPEE_DEE;
        break;
      case CarrierServiceCodeConstant.PASSPORT_GLOBAL:
        carrier = Carriers.PASSPORT_GLOBAL;
        break;
      case CarrierServiceCodeConstant.LANDMARK_GLOBAL:
        carrier = Carriers.LANDMARK_GLOBAL;
        break;
      case CarrierServiceCodeConstant.ETA_EXPRESS:
        carrier = Carriers.ETA_EXPRESS;
        break;
      case CarrierServiceCodeConstant.EPOST_GLOBAL:
        carrier = Carriers.EPOST_GLOBAL;
        break;
      case CarrierServiceCodeConstant.ONTRAC:
        carrier = Carriers.ONTRAC;
        break;
      case CarrierServiceCodeConstant.GLOBALE:
        carrier = Carriers.GLOBALE;
        break;
      case CarrierServiceCodeConstant.UPS_MAIL_INNOVATIONS:
        carrier = Carriers.UPS_MAIL_INNOVATIONS;
        break;
      case CarrierServiceCodeConstant.PUROLATOR:
        carrier = Carriers.PUROLATOR;
        break;
      case CarrierServiceCodeConstant.UNIUNI:
        carrier = Carriers.UNIUNI;
        break;
      case CarrierServiceCodeConstant.STALLION_EXPRESS:
        carrier = Carriers.STALLION_EXPRESS;
        break;
      case CarrierServiceCodeConstant.CHITCHATS:
        carrier = Carriers.CHITCHATS;
        break;
      case CarrierServiceCodeConstant.ECOM_EXPRESS:
        carrier = Carriers.ECOM_EXPRESS;
        break;
      case CarrierServiceCodeConstant.POST_NL:
        carrier = Carriers.POST_NL;
        break;
      case CarrierServiceCodeConstant.INTELCOM:
        carrier = Carriers.INTELCOM;
        break;
      case CarrierServiceCodeConstant.FLEET_OPTICS:
        carrier = Carriers.FLEET_OPTICS;
        break;
      case CarrierServiceCodeConstant.ICS_COURIER:
        carrier = Carriers.ICS_COURIER;
        break;
      case CarrierServiceCodeConstant.WHIZ_DELIVERY:
        carrier = Carriers.WHIZ_DELIVERY;
        break;
      case CarrierServiceCodeConstant.TIKTOK_SHOP:
        carrier = Carriers.TIKTOK_SHOP;
        break;
      case CarrierServiceCodeConstant.CANPAR_EXPRESS:
        carrier = Carriers.CANPAR_EXPRESS;
        break;
      default:
        assertNever(carrierServiceCodeConstant);
    }

    if (!carrier) {
      throw new Error("Invalid carrier");
    }

    const carrierService = CorrelatedCarrierServiceSchema.parse({
      carrier,
      service,
    });

    let markup:
      | { amount?: string | undefined; percentage?: string | undefined }
      | undefined;
    if (redoServiceCode[COST_BREAKDOWN_KEY][MARKUP_KEY]?.[MARKUP_AMOUNT_KEY]) {
      markup = {
        ...markup,
        amount:
          redoServiceCode[COST_BREAKDOWN_KEY][MARKUP_KEY][MARKUP_AMOUNT_KEY],
      };
    }
    if (
      redoServiceCode[COST_BREAKDOWN_KEY][MARKUP_KEY]?.[MARKUP_PERCENTAGE_KEY]
    ) {
      markup = {
        ...markup,
        percentage:
          redoServiceCode[COST_BREAKDOWN_KEY][MARKUP_KEY][
            MARKUP_PERCENTAGE_KEY
          ],
      };
    }

    let rounding: DynamicRateRounding | undefined = undefined;
    if (
      redoServiceCode[COST_BREAKDOWN_KEY][ROUNDING_KEY] &&
      isDynamicRateRoundingServiceCodeSerializedKey(
        redoServiceCode[COST_BREAKDOWN_KEY][ROUNDING_KEY],
      )
    ) {
      rounding =
        dynamicRateRoundingToServiceCodeDeserialization[
          redoServiceCode[COST_BREAKDOWN_KEY][ROUNDING_KEY]
        ];
    }

    const promisedDeliveryDate = redoServiceCode[
      PROMISED_DELIVERY_DATE_IN_YYYYMMDD_KEY
    ]
      ? parsePromisedDeliveryDate(
          redoServiceCode[PROMISED_DELIVERY_DATE_IN_YYYYMMDD_KEY],
        )
      : undefined;

    return {
      provider: redoServiceCode[PROVIDER_KEY],
      rateTableId: redoServiceCode[RATE_TABLE_ID_KEY],
      carrierService,
      costBreakdown: {
        currency: redoServiceCode[COST_BREAKDOWN_KEY][COST_CURRENCY_KEY],
        labelCost: redoServiceCode[COST_BREAKDOWN_KEY][LABEL_COST_KEY],
        markup,
        rounding,
      },
      assignedTreatmentIds: redoServiceCode[ASSIGNED_TREATMENT_IDS_KEY],
      promisedDeliveryDate,
    };
  } catch {
    return null;
  }
}

export function parseFixedShippingMethodCode(
  code: string,
): {
  provider: "redo";
  rateTableId: string;
  assignedTreatmentIds?: string[] | undefined;
  checkoutTreeNodeId: string;
  documentId: string | undefined;
} | null {
  try {
    const parsed = JSON.parse(code);
    const redoServiceCode = RedoFlatRateServiceCodeSchema.parse(parsed);

    return {
      provider: redoServiceCode[PROVIDER_KEY],
      rateTableId: redoServiceCode[RATE_TABLE_ID_KEY],
      assignedTreatmentIds: redoServiceCode[ASSIGNED_TREATMENT_IDS_KEY],
      checkoutTreeNodeId: redoServiceCode[CHECKOUT_TREE_NODE_ID_KEY],
      documentId: redoServiceCode[CompactServiceCodeKey.DOCUMENT_ID],
    };
  } catch {
    return null;
  }
}

export function parseShippingMethodCode(
  code?: string | null,
):
  | {
      parsedInfo: NonNullable<
        ReturnType<typeof parseDynamicShippingMethodCode>
      >;
      type: "legacy-dynamic";
    }
  | {
      parsedInfo: NonNullable<ReturnType<typeof parseFixedShippingMethodCode>>;
      type: "fixed";
    }
  | { parsedInfo: NonNullable<CompactServiceCodeParsedInfo>; type: "compact" }
  | null {
  if (!code) {
    return null;
  }
  // THE ORDER OF THESE PARSE ATTEMPTS MATTERS
  // If you ever think about changing the order, thoroughly consider how it will
  // change the behavior when dynamic/fixed/compact service codes are passed into
  // this function. E.g. if you the compact parse comes after the fixed parse,
  // and you pass in a compact code, it will "successfully" parse as a fixed code.
  const compact = parseCompactServiceCode(code);
  if (compact) {
    return { parsedInfo: compact, type: "compact" };
  }

  const dynamic = parseDynamicShippingMethodCode(code);
  if (dynamic) {
    return { parsedInfo: dynamic, type: "legacy-dynamic" };
  }

  const fixed = parseFixedShippingMethodCode(code);
  if (fixed) {
    return { parsedInfo: fixed, type: "fixed" };
  }

  return null;
}

/**
 * @returns e.g. "1.20250627T12:37[America/Denver]"
 */
function serializePromisedDeliveryDate(
  promisedDeliveryDate: PromisedDeliveryDate,
): string {
  const { date, visibleToCustomer } = promisedDeliveryDate;
  const datePart = date.toPlainDate().toString().replace(/-/g, ""); // e.g. 20250627
  const timePart = date.toPlainTime().toString().slice(0, 5); // e.g. "12:37"
  const timeZone = date.timeZoneId; // e.g. "America/Denver"

  return `${visibleToCustomer ? "1" : "0"}.${datePart}T${timePart}[${timeZone}]`;
}

/**
 * Needs to handle forms
 * - "1.20250616" (from before 06/18/25)
 * - "1.20250627T12:37:30.65806:00[America/Denver]" (from between 06/18/25 and 06/23/25)
 * - "1.20250627T12:37[UTC]" (from after 06/23/25)
 */
export function parsePromisedDeliveryDate(
  serializedPromisedDeliveryDate: string,
): PromisedDeliveryDate {
  const [visibleToCustomer, date] = serializedPromisedDeliveryDate.split(
    ".",
    2,
  );

  // Match form: "20250627T12:37[America/Denver]"
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  const match = date.match(/^(\d{8})T(\d{2}:\d{2})\[(.+)\]$/);
  if (match) {
    const [, datePart, timePart, timeZone] = match;
    // Reformat "20250627" → "2025-06-27"
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    const isoDate = `${datePart.slice(0, 4)}-${datePart.slice(4, 6)}-${datePart.slice(6, 8)}`;
    const isoDateTime = `${isoDate}T${timePart}`;

    return {
      visibleToCustomer: visibleToCustomer === "1",
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      date: Temporal.PlainDateTime.from(isoDateTime).toZonedDateTime(timeZone),
    };
  }

  // Match form: "1.20250627T12:37:30.65806:00[America/Denver]"
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  if (date.includes("T")) {
    // Just get the 20250627 part
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    const dateStr = date.split("T")[0];
    return {
      visibleToCustomer: visibleToCustomer === "1",
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      date: Temporal.PlainDate.from(dateStr).toZonedDateTime("UTC"),
    };
  }

  return {
    visibleToCustomer: visibleToCustomer === "1",
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    date: Temporal.PlainDate.from(date).toZonedDateTime("UTC"),
  };
}

/**
 * Creates a CheckoutShippingOption from legacy DynamicRateServiceCodeParams
 * This is used when transitioning from the old system to the new compact system.
 */
export function createCheckoutShippingOptionFromLegacyParams(
  params: DynamicRateServiceCodeParams,
  logError: (message: string) => void,
): CheckoutShippingOption {
  if (params.assignedTreatmentIds && params.assignedTreatmentIds.length > 1) {
    logError("Multiple treatment IDs are not supported");
  }

  // Extract the treatment ID (should only be one)
  const treatmentId = params.assignedTreatmentIds?.[0];

  const checkoutShippingOption: CheckoutShippingOption = {
    provider: "redo",
    rateTableId: params.rateTableId,
    checkoutTreeNodeId: params.checkoutTreeNodeId,
    checkoutExperienceId: params.checkoutExperienceId,
    assignedTreatmentId: treatmentId,
    rateType: RateTableType.DYNAMIC,
    carrierService: params.carrierService,
    carrierServices: params.carrierServices,
    costBreakdown: {
      costCurrency: params.pricing.currency,
      labelCost: params.pricing.labelCost,
      markup: params.pricing.markup
        ? {
            markupPercentage: params.pricing.markup.percentage,
            markupAmount: params.pricing.markup.amount,
          }
        : undefined,
      rounding: params.pricing.rounding,
    },
    promisedDeliveryDate: serializePromisedDeliveryDate(
      params.promisedDeliveryDate,
    ),
    coverageTypes: params.coverageTypes,
  };

  return checkoutShippingOption;
}
